/**
 * Commission Cascade Audit Component
 *
 * READ-ONLY audit of the multi-level commission cascade for any date range.
 * Verifies that all completed purchases have correctly triggered and recorded commissions.
 *
 * Features:
 * - Date range selector
 * - Run Audit button
 * - Expandable purchase details with expected vs actual commissions
 * - Conversion audit (known bug area)
 * - CSV export
 * - Summary statistics
 * - Pagination with configurable page size
 * - Filtering by username, level, and amount
 * - Sortable columns
 * - Bulk operations with progress tracking
 * - Export current view
 * - Auto-refresh option
 * - Undo recent actions
 */

import React, { useState, useCallback, useEffect, useRef } from 'react';
import { getSessionHeaders } from '../../lib/serverSession';

// Constants
const BULK_SELECTION_LIMIT = 500;
const PAGE_SIZE_OPTIONS = [25, 50, 100, 250, 500];
const DEFAULT_PAGE_SIZE = 25;
const AUTO_REFRESH_INTERVALS = [
  { label: 'Off', value: 0 },
  { label: '1 min', value: 60000 },
  { label: '5 min', value: 300000 },
  { label: '15 min', value: 900000 }
];

// Local storage keys for persisting preferences
const STORAGE_KEYS = {
  PAGE_SIZE: 'audit_page_size',
  AUTO_REFRESH: 'audit_auto_refresh',
  FILTERS: 'audit_filters'
};

// Types for filtering and sorting
interface FilterState {
  usernameSearch: string;
  levelFilter: number | null;
  minAmount: number | null;
  quickFilter: 'all' | 'today' | 'l1-only' | 'high-value';
}

interface SortState {
  column: 'date' | 'level' | 'amount' | 'referrer' | null;
  direction: 'asc' | 'desc';
}

// Type for undo actions
interface UndoAction {
  type: 'fix' | 'ignore';
  discrepancy: Discrepancy;
  timestamp: Date;
  commissionId?: string;
}

// Type for bulk operation progress
interface BulkProgress {
  current: number;
  total: number;
  failures: Array<{ discrepancy: Discrepancy; error: string }>;
}

interface AuditSummary {
  totalMissingUSDT: number;
  totalMissingShares: number;
  missingCommissionCount: number;
  incorrectUSDTCount: number;
  incorrectShareCount: number;
  wrongPricingCount: number;
  extraCommissionCount: number;
}

interface ExpectedCommission {
  level: number;
  referrerId: number;
  referrerUsername: string;
  expectedUSDT: number;
  expectedShares: number;
  baseAmount: number;
  sharePurchaseValue?: number;
}

interface ActualCommission {
  level: number;
  referrerId: number;
  actualUSDT: number;
  actualShares: number;
}

interface Discrepancy {
  type: string;
  level: number;
  referrerId: number;
  referrerUsername: string;
  expectedUSDT?: number;
  expectedShares?: number;
  actualUSDT?: number;
  actualShares?: number;
  difference?: number;
  baseAmount?: number;
  message: string;
  // Additional fields for fix functionality
  sourceType?: string;
  sourceId?: string;
  referredId?: number;
  sharePrice?: number;
  phaseId?: number; // Phase ID for determining commission structure
  // Triggered by info - who made the purchase/conversion
  triggeredByUserId?: number;
  triggeredByUsername?: string;
  purchaseAmount?: number;
  conversionAmount?: number;
  // WRONG_REFERRER fields - for cases where commission went to wrong person
  wrongReferrerId?: number;
  wrongReferrerUsername?: string;
  existingCommissionId?: string;
  // Date when the triggering event occurred
  createdAt?: string;
  // Enhanced diagnostic fields (2026-01-08)
  failureReason?: string;
  approvalDate?: string;
}

interface PurchaseDetail {
  purchaseId: string;
  userId: number;
  username: string;
  amount: number;
  shares: number;
  paymentMethod: string;
  phaseId: number;
  phaseName: string;
  pricePerShare: number;
  priceSource: 'purchase' | 'purchase_snapshot' | 'phase' | 'default';
  createdAt: string;
  shouldTriggerCommission: boolean;
  exclusionReason: string | null;
  expectedCommissions: ExpectedCommission[];
  actualCommissions: ActualCommission[];
  discrepancies: Discrepancy[];
}

interface ConversionAudit {
  conversionId: string;
  userId: number;
  username: string;
  usdtAmount: number;
  sharesRequested: number;
  sharePrice: number;
  createdAt: string;
  approvedAt?: string;
  shouldTriggerCommission: boolean;
  exclusionReason?: string;
  phaseId?: number;
  commissionStructure?: string;
  expectedCommissions: ExpectedCommission[];
  actualCommissions: ActualCommission[];
  discrepancies: Discrepancy[];
  // Enhanced diagnostic info (2026-01-08)
  diagnostics?: {
    approvalDate: string;
    matchingAttempts: number;
    possibleMatches: Array<{ level: number; strategy: string; commissionId?: string; timeDiffHours?: number }>;
    failureReason: string | null;
  };
}

interface AuditError {
  purchaseId?: string;
  conversionId?: string;
  error: string;
}

interface FilteredUser {
  id: number;
  username: string;
  email: string;
}

interface AuditResults {
  auditPeriod: { startDate: string; endDate: string };
  filteredUser?: FilteredUser | null;
  totalTransactionsAudited: number;
  transactionsWithCommissions: number;
  excludedTransactions: number;
  discrepancies: Discrepancy[];
  summary: AuditSummary;
  conversionAudit: ConversionAudit[];
  pricingIssues: any[];
  exclusionVerification: { visa: any[]; topups: any[] };
  purchaseDetails: PurchaseDetail[];
  auditErrors?: AuditError[];
}

export function CommissionCascadeAudit() {
  // Date range state - default to current month (dynamic)
  const [startDate, setStartDate] = useState(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-01`;
  });
  const [endDate, setEndDate] = useState(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
  });

  // User search state
  const [userSearch, setUserSearch] = useState('');

  // Audit state
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [results, setResults] = useState<AuditResults | null>(null);

  // UI state
  const [expandedPurchaseId, setExpandedPurchaseId] = useState<string | null>(null);
  const [_expandedConversionId, _setExpandedConversionId] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'summary' | 'purchases' | 'presale-conversions' | 'multilevel-conversions' | 'purchase-issues' | 'conversion-issues'>('summary');
  const [filterDiscrepanciesOnly, setFilterDiscrepanciesOnly] = useState(false);
  const [exporting, setExporting] = useState(false);

  // Fix commission state
  const [fixingCommission, setFixingCommission] = useState<string | null>(null);
  const [fixResult, setFixResult] = useState<{
    success: boolean;
    message: string;
    validation?: {
      valid: boolean;
      checks: Array<{ name: string; passed: boolean; details: string }>;
      message: string;
    };
  } | null>(null);

  // Bulk fix state
  const [selectedForFix, setSelectedForFix] = useState<Set<string>>(new Set());
  const [bulkFixing, setBulkFixing] = useState(false);
  const [bulkProgress, setBulkProgress] = useState<BulkProgress | null>(null);

  // Filter and sort state
  const [filters, _setFilters] = useState<FilterState>({
    usernameSearch: '',
    levelFilter: null,
    minAmount: null,
    quickFilter: 'all'
  });
  const [sortState, _setSortState] = useState<SortState>({ column: null, direction: 'desc' });

  // Pagination state
  const [pageSize, _setPageSize] = useState<number>(() => {
    if (typeof window !== 'undefined') {
      const saved = localStorage.getItem(STORAGE_KEYS.PAGE_SIZE);
      return saved ? parseInt(saved) : DEFAULT_PAGE_SIZE;
    }
    return DEFAULT_PAGE_SIZE;
  });
  const [_currentPage, setCurrentPage] = useState(1);

  // Auto-refresh state
  const [autoRefreshInterval, setAutoRefreshInterval] = useState<number>(() => {
    if (typeof window !== 'undefined') {
      const saved = localStorage.getItem(STORAGE_KEYS.AUTO_REFRESH);
      return saved ? parseInt(saved) : 0;
    }
    return 0;
  });
  const autoRefreshTimerRef = useRef<NodeJS.Timeout | null>(null);

  // Undo state
  const [undoStack, setUndoStack] = useState<UndoAction[]>([]);
  const [undoing, setUndoing] = useState(false);

  // Confirmation modal state
  // v4.8.22 #6: discriminated union — 'single-fix' carries the full FixCommissionParams
  // payload so the modal can show every value the admin is about to commit.
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [confirmModalAction, setConfirmModalAction] = useState<
    | {
        type: 'bulk-fix' | 'bulk-ignore';
        count: number;
        onConfirm: (opts?: { suppressNotifications?: boolean }) => void;
      }
    | {
        type: 'single-fix';
        params: FixCommissionParams;
        onConfirm: () => void;
      }
    | null
  >(null);

  // v4.8.21: Bulk-fix notification suppression toggle. Defaults to TRUE because the
  // primary use case is back-filling stale (multi-day-old) commissions where firing a
  // wave of "you earned a commission" emails for events users have already seen would
  // be confusing. Admin can untick to opt back into notifications.
  const [bulkSuppressNotifications, setBulkSuppressNotifications] = useState(true);

  // Persist page size preference
  useEffect(() => {
    if (typeof window !== 'undefined') {
      localStorage.setItem(STORAGE_KEYS.PAGE_SIZE, pageSize.toString());
    }
  }, [pageSize]);

  // Persist auto-refresh preference
  useEffect(() => {
    if (typeof window !== 'undefined') {
      localStorage.setItem(STORAGE_KEYS.AUTO_REFRESH, autoRefreshInterval.toString());
    }
  }, [autoRefreshInterval]);

  // Run audit function - defined first so handleFixCommission can reference it
  const runAudit = useCallback(async () => {
    setLoading(true);
    setError('');
    setResults(null);

    try {
      const requestBody: { startDate: string; endDate: string; username?: string } = {
        startDate,
        endDate
      };

      // Add user filter if searching for specific user
      if (userSearch.trim()) {
        requestBody.username = userSearch.trim();
      }

      const response = await fetch('/api/admin/commission-audit', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getSessionHeaders() },
        body: JSON.stringify(requestBody)
      });

      if (!response.ok) {
        const errData = await response.json();
        throw new Error(errData.error || errData.message || 'Audit failed');
      }

      const data = await response.json();
      setResults(data);
      setActiveTab('summary');
    } catch (err: any) {
      setError(err.message || 'Failed to run audit');
    } finally {
      setLoading(false);
    }
  }, [startDate, endDate, userSearch]);

  // Helper function to remove a discrepancy from results after successful fix/ignore
  const removeDiscrepancyFromResults = useCallback((fixKey: string) => {
    if (!results) return;

    // NOTE: Do NOT split fixKey by '-' to parse parts — source IDs are UUIDs that contain
    // hyphens, which breaks destructuring. Instead, reconstruct each discrepancy's key and
    // compare the full string directly (same approach used in purchaseDetails/conversionAudit below).
    setResults(prevResults => {
      if (!prevResults) return null;

      const newDiscrepancies = prevResults.discrepancies.filter(d => {
        const key = `${d.sourceType}-${d.sourceId}-L${d.level}-${d.referrerId}`;
        return key !== fixKey;
      });

      // Also update purchase details and conversion audit
      const newPurchaseDetails = prevResults.purchaseDetails.map(p => ({
        ...p,
        discrepancies: p.discrepancies.filter(d => {
          const key = `purchase-${p.purchaseId}-L${d.level}-${d.referrerId}`;
          return key !== fixKey;
        })
      }));

      const newConversionAudit = prevResults.conversionAudit.map(c => ({
        ...c,
        discrepancies: c.discrepancies.filter(d => {
          const key = `conversion-${c.conversionId}-L${d.level}-${d.referrerId}`;
          return key !== fixKey;
        })
      }));

      return {
        ...prevResults,
        discrepancies: newDiscrepancies,
        purchaseDetails: newPurchaseDetails,
        conversionAudit: newConversionAudit
      };
    });
  }, [results]);

  // Auto-refresh effect
  useEffect(() => {
    if (autoRefreshTimerRef.current) {
      clearInterval(autoRefreshTimerRef.current);
      autoRefreshTimerRef.current = null;
    }

    if (autoRefreshInterval > 0 && results) {
      autoRefreshTimerRef.current = setInterval(() => {
        runAudit();
      }, autoRefreshInterval);
    }

    return () => {
      if (autoRefreshTimerRef.current) {
        clearInterval(autoRefreshTimerRef.current);
      }
    };
  }, [autoRefreshInterval, results, runAudit]);

  // Undo last action handler
  const handleUndo = useCallback(async () => {
    if (undoStack.length === 0) return;

    const lastAction = undoStack[undoStack.length - 1];
    setUndoing(true);

    try {
      if (lastAction.type === 'ignore' && lastAction.commissionId) {
        // Delete the ignored record to re-enable the discrepancy
        const response = await fetch('/api/admin/commission-fix', {
          method: 'DELETE',
          headers: { 'Content-Type': 'application/json', ...getSessionHeaders() },
          body: JSON.stringify({ commissionId: lastAction.commissionId })
        });

        if (!response.ok) {
          throw new Error('Failed to undo ignore action');
        }

        // Re-run audit to refresh data
        await runAudit();
        setFixResult({ success: true, message: 'Successfully undid last ignore action' });
      } else {
        // For fix actions, we'd need to delete the created commission
        // This is more complex and may not be safe - show warning instead
        setFixResult({ success: false, message: 'Undo for fix actions requires manual intervention' });
      }

      // Remove from undo stack
      setUndoStack(prev => prev.slice(0, -1));
    } catch (err: any) {
      setFixResult({ success: false, message: err.message || 'Failed to undo action' });
    } finally {
      setUndoing(false);
    }
  }, [undoStack, runAudit]);

  // Add action to undo stack
  const addToUndoStack = useCallback((action: UndoAction) => {
    setUndoStack(prev => {
      // Keep only last 10 actions
      const newStack = [...prev, action].slice(-10);
      return newStack;
    });
  }, []);

  // Reset page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [filters, sortState]);

  // Execute a single-row fix (after admin confirms the modal preview).
  // v4.8.22 #6: this is the actual POST. The `display` field is UI-only and
  // is stripped before the request body is built.
  const executeFixCommission = useCallback(async (params: FixCommissionParams) => {
    const fixKey = `${params.type}-${params.sourceId}-L${params.level}-${params.referrerId}`;
    setFixingCommission(fixKey);
    setFixResult(null);

    // Strip UI-only `display` block from the payload sent to the backend.
    const { display: _displayIgnored, ...payload } = params;

    try {
      const response = await fetch('/api/admin/fix-missing-commission', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getSessionHeaders() },
        body: JSON.stringify(payload)
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || data.message || 'Fix failed');
      }

      // For INCORRECT types, also add to ignore list so it doesn't show again
      // (the original wrong commission will still exist, but we've added the difference)
      if (payload.discrepancyType?.includes('INCORRECT')) {
        await fetch('/api/admin/ignore-discrepancy', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', ...getSessionHeaders() },
          body: JSON.stringify({
            discrepancyType: payload.discrepancyType,
            sourceType: payload.type,
            sourceId: payload.sourceId,
            referrerId: payload.referrerId,
            level: payload.level,
            reason: 'Auto-ignored after fix - difference corrected'
          })
        });
      }

      setFixResult({
        success: true,
        message: data.message || 'Commission fixed successfully!',
        validation: data.validation
      });

      // Remove the fixed discrepancy from local state immediately
      removeDiscrepancyFromResults(fixKey);
    } catch (err: any) {
      setFixResult({ success: false, message: err.message || 'Failed to fix commission' });
    } finally {
      setFixingCommission(null);
    }
  }, [removeDiscrepancyFromResults]);

  // Fix missing commission handler — opens the per-row confirmation modal.
  // v4.8.22 #6: never POSTs directly; admin must confirm the payload preview first.
  const handleFixCommission = useCallback(async (params: FixCommissionParams) => {
    setConfirmModalAction({
      type: 'single-fix',
      params,
      onConfirm: () => {
        setShowConfirmModal(false);
        void executeFixCommission(params);
      }
    });
    setShowConfirmModal(true);
  }, [executeFixCommission]);

  // Toggle selection for bulk fix
  const toggleSelection = useCallback((key: string, _discrepancy: Discrepancy) => {
    setSelectedForFix(prev => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else if (next.size < BULK_SELECTION_LIMIT) {
        next.add(key);
      }
      return next;
    });
  }, []);

  // Bulk fix handler
  // Execute bulk fix (called after confirmation)
  // v4.8.21: accepts suppressNotifications flag, passed through to the fix API so the
  // email cron skips the per-row notification for back-filled stale events.
  const executeBulkFix = useCallback(async (selected: Discrepancy[], suppressNotifications: boolean) => {
    setBulkFixing(true);
    setFixResult(null);
    setBulkProgress({ current: 0, total: selected.length, failures: [] });

    let successCount = 0;
    const successfulKeys: string[] = [];
    const failures: Array<{ discrepancy: Discrepancy; error: string }> = [];

    for (let i = 0; i < selected.length; i++) {
      const d = selected[i];
      const fixKey = `${d.sourceType}-${d.sourceId}-L${d.level}-${d.referrerId}`;

      // Update progress
      setBulkProgress(prev => prev ? { ...prev, current: i + 1 } : null);

      try {
        const isIncorrect = d.type.includes('INCORRECT');

        let fixUSDT = 0;
        let fixShares = 0;
        if (d.type === 'INCORRECT_USDT' || d.type === 'INCORRECT_CONVERSION_USDT') {
          fixUSDT = d.difference || 0;
        } else if (d.type === 'INCORRECT_SHARES' || d.type === 'INCORRECT_CONVERSION_SHARES') {
          fixShares = d.difference || 0;
        } else {
          fixUSDT = d.expectedUSDT || 0;
          fixShares = d.expectedShares || 0;
        }

        const response = await fetch('/api/admin/fix-missing-commission', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', ...getSessionHeaders() },
          body: JSON.stringify({
            type: d.sourceType as 'purchase' | 'conversion',
            sourceId: d.sourceId || '',
            referredId: d.referredId || 0,
            referrerId: d.referrerId,
            level: d.level,
            expectedUSDT: fixUSDT,
            expectedShares: fixShares,
            baseAmount: d.baseAmount || 0,
            phaseId: d.phaseId || 2,
            sharePrice: d.sharePrice || 15, // Phase 2 default per project policy ($15/share)
            suppressNotifications // v4.8.21: forwarded to api/admin/fix-missing-commission.js
          })
        });

        if (response.ok) {
          if (isIncorrect) {
            await fetch('/api/admin/ignore-discrepancy', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', ...getSessionHeaders() },
              body: JSON.stringify({
                discrepancyType: d.type,
                sourceType: d.sourceType,
                sourceId: d.sourceId,
                referrerId: d.referrerId,
                level: d.level,
                reason: 'Auto-ignored after bulk fix - difference corrected'
              })
            });
          }
          successCount++;
          successfulKeys.push(fixKey);
        } else {
          const errData = await response.json().catch(() => ({}));
          failures.push({ discrepancy: d, error: errData.error || 'Unknown error' });
        }
      } catch (err: any) {
        failures.push({ discrepancy: d, error: err.message || 'Network error' });
      }

      // Update failures in progress
      setBulkProgress(prev => prev ? { ...prev, failures } : null);
    }

    for (const key of successfulKeys) {
      removeDiscrepancyFromResults(key);
    }

    setSelectedForFix(new Set());
    setBulkFixing(false);

    const failCount = failures.length;
    let message = `Bulk fix complete: ${successCount} fixed, ${failCount} failed`;
    if (failCount > 0) {
      message += `. Failed: ${failures.map(f => f.discrepancy.referrerUsername || f.discrepancy.referrerId).join(', ')}`;
    }

    setFixResult({ success: failCount === 0, message });
    setBulkProgress(null);
  }, [removeDiscrepancyFromResults]);

  // Bulk fix handler with confirmation
  const handleBulkFix = useCallback(async (discrepancies: Discrepancy[]) => {
    const selected = discrepancies.filter(d => {
      const key = `${d.sourceType}-${d.sourceId}-L${d.level}-${d.referrerId}`;
      return selectedForFix.has(key);
    });

    if (selected.length === 0) return;

    // Show confirmation modal
    // v4.8.21: the modal renders a "suppress notifications" checkbox bound to
    // bulkSuppressNotifications; the latest value is read at confirm-time via the
    // onConfirm argument so the user's last interaction is honoured.
    setConfirmModalAction({
      type: 'bulk-fix',
      count: selected.length,
      onConfirm: (opts) => {
        setShowConfirmModal(false);
        executeBulkFix(selected, opts?.suppressNotifications ?? true);
      }
    });
    setShowConfirmModal(true);
  }, [selectedForFix, executeBulkFix]);

  // Bulk ignore state
  const [bulkIgnoring, setBulkIgnoring] = useState(false);

  // Execute bulk ignore (called after confirmation)
  const executeBulkIgnore = useCallback(async (selected: Discrepancy[]) => {
    setBulkIgnoring(true);
    setFixResult(null);
    setBulkProgress({ current: 0, total: selected.length, failures: [] });

    let successCount = 0;
    const successfulKeys: string[] = [];
    const failures: Array<{ discrepancy: Discrepancy; error: string }> = [];

    for (let i = 0; i < selected.length; i++) {
      const d = selected[i];
      const ignoreKey = `${d.sourceType}-${d.sourceId}-L${d.level}-${d.referrerId}`;

      setBulkProgress(prev => prev ? { ...prev, current: i + 1 } : null);

      try {
        const response = await fetch('/api/admin/ignore-discrepancy', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', ...getSessionHeaders() },
          body: JSON.stringify({
            discrepancyType: d.type,
            sourceType: d.sourceType,
            sourceId: d.sourceId,
            referrerId: d.referrerId,
            level: d.level,
            expectedUSDT: d.expectedUSDT,
            actualUSDT: d.actualUSDT,
            expectedShares: d.expectedShares,
            actualShares: d.actualShares,
            difference: d.difference,
            triggeredByUserId: d.triggeredByUserId,
            purchaseAmount: d.purchaseAmount,
            reason: 'Bulk ignore - accepted as is'
          })
        });

        if (response.ok) {
          successCount++;
          successfulKeys.push(ignoreKey);
        } else {
          const errData = await response.json().catch(() => ({}));
          failures.push({ discrepancy: d, error: errData.error || 'Unknown error' });
        }
      } catch (err: any) {
        failures.push({ discrepancy: d, error: err.message || 'Network error' });
      }

      setBulkProgress(prev => prev ? { ...prev, failures } : null);
    }

    for (const key of successfulKeys) {
      removeDiscrepancyFromResults(key);
    }

    setSelectedForFix(new Set());
    setBulkIgnoring(false);

    const failCount = failures.length;
    let message = `Bulk ignore complete: ${successCount} ignored, ${failCount} failed`;
    if (failCount > 0) {
      message += `. Failed: ${failures.map(f => f.discrepancy.referrerUsername || f.discrepancy.referrerId).join(', ')}`;
    }

    setFixResult({ success: failCount === 0, message });
    setBulkProgress(null);
  }, [removeDiscrepancyFromResults]);

  // Bulk ignore handler with confirmation
  const handleBulkIgnore = useCallback(async (discrepancies: Discrepancy[]) => {
    const selected = discrepancies.filter(d => {
      const key = `${d.sourceType}-${d.sourceId}-L${d.level}-${d.referrerId}`;
      return selectedForFix.has(key);
    });

    if (selected.length === 0) return;

    setConfirmModalAction({
      type: 'bulk-ignore',
      count: selected.length,
      onConfirm: () => {
        setShowConfirmModal(false);
        executeBulkIgnore(selected);
      }
    });
    setShowConfirmModal(true);
  }, [selectedForFix, executeBulkIgnore]);

  // Ignore discrepancy state
  const [ignoringDiscrepancy, setIgnoringDiscrepancy] = useState<string | null>(null);

  // Handle ignoring a discrepancy (for overpayments we choose to accept)
  const handleIgnoreDiscrepancy = useCallback(async (discrepancy: Discrepancy) => {
    const ignoreKey = `${discrepancy.sourceType}-${discrepancy.sourceId}-L${discrepancy.level}-${discrepancy.referrerId}`;
    setIgnoringDiscrepancy(ignoreKey);
    setFixResult(null);

    try {
      const response = await fetch('/api/admin/ignore-discrepancy', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getSessionHeaders() },
        body: JSON.stringify({
          discrepancyType: discrepancy.type,
          sourceType: discrepancy.sourceType,
          sourceId: discrepancy.sourceId,
          referrerId: discrepancy.referrerId,
          level: discrepancy.level,
          expectedUSDT: discrepancy.expectedUSDT,
          actualUSDT: discrepancy.actualUSDT,
          expectedShares: discrepancy.expectedShares,
          actualShares: discrepancy.actualShares,
          difference: discrepancy.difference,
          triggeredByUserId: discrepancy.triggeredByUserId,
          purchaseAmount: discrepancy.purchaseAmount,
          reason: 'Overpayment accepted - will not claw back'
        })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || data.message || 'Failed to ignore discrepancy');
      }

      setFixResult({ success: true, message: 'Discrepancy ignored! It will not appear in future audits.' });

      // Remove the ignored discrepancy from local state immediately
      removeDiscrepancyFromResults(ignoreKey);

      // Add to undo stack
      addToUndoStack({
        type: 'ignore',
        discrepancy,
        timestamp: new Date(),
        commissionId: data.recordId // Assuming API returns the created record ID
      });
    } catch (err: any) {
      setFixResult({ success: false, message: err.message || 'Failed to ignore discrepancy' });
    } finally {
      setIgnoringDiscrepancy(null);
    }
  }, [removeDiscrepancyFromResults, addToUndoStack]);

  const exportToCSV = async () => {
    if (!results) return;
    setExporting(true);

    try {
      // Build CSV content
      const lines: string[] = [];

      // Header
      lines.push('Commission Cascade Audit Report');
      lines.push(`Period: ${results.auditPeriod.startDate} to ${results.auditPeriod.endDate}`);
      lines.push(`Generated: ${new Date().toISOString()}`);
      lines.push('');

      // Summary
      lines.push('=== SUMMARY ===');
      lines.push(`Total Transactions Audited,${results.totalTransactionsAudited}`);
      lines.push(`Transactions With Commissions,${results.transactionsWithCommissions}`);
      lines.push(`Excluded Transactions,${results.excludedTransactions}`);
      lines.push(`Missing Commission Count,${results.summary.missingCommissionCount}`);
      lines.push(`Total Missing USDT,$${results.summary.totalMissingUSDT}`);
      lines.push(`Total Missing Shares,${results.summary.totalMissingShares}`);
      lines.push(`Incorrect USDT Count,${results.summary.incorrectUSDTCount}`);
      lines.push(`Incorrect Shares Count,${results.summary.incorrectShareCount}`);
      lines.push('');

      // Purchase Details
      lines.push('=== PURCHASE DETAILS ===');
      lines.push('Purchase ID,User ID,Username,Amount,Shares,Payment Method,Phase,Price Per Share,Price Source,Date,Should Trigger,Exclusion Reason,Discrepancy Count');
      for (const p of results.purchaseDetails) {
        lines.push([
          `"${p.purchaseId}"`,
          p.userId,
          `"${p.username}"`,
          p.amount,
          p.shares,
          `"${p.paymentMethod}"`,
          `"${p.phaseName}"`,
          p.pricePerShare,
          `"${p.priceSource || 'unknown'}"`,
          `"${p.createdAt}"`,
          p.shouldTriggerCommission,
          `"${p.exclusionReason || ''}"`,
          p.discrepancies.length
        ].join(','));
      }
      lines.push('');

      // Discrepancies
      lines.push('=== DISCREPANCIES ===');
      lines.push('Type,Level,Referrer ID,Referrer Username,Expected USDT,Expected Shares,Actual USDT,Actual Shares,Message');
      for (const d of results.discrepancies) {
        lines.push([
          d.type,
          d.level,
          d.referrerId,
          `"${d.referrerUsername}"`,
          d.expectedUSDT || '',
          d.expectedShares || '',
          d.actualUSDT || '',
          d.actualShares || '',
          `"${d.message}"`
        ].join(','));
      }
      lines.push('');

      // Conversion Audit - Split by Phase
      lines.push('=== PRE-SALE CONVERSIONS (15% + 15%) ===');
      lines.push('Conversion ID,User ID,Username,USDT Amount,Shares Requested,Share Price,Phase,Commission Structure,Date,Should Trigger,Exclusion Reason,Discrepancy Count');
      const presaleConversions = results.conversionAudit.filter(c => c.phaseId === 1);
      for (const c of presaleConversions) {
        lines.push([
          `"${c.conversionId}"`,
          c.userId,
          `"${c.username}"`,
          c.usdtAmount,
          c.sharesRequested,
          c.sharePrice,
          c.phaseId,
          `"${c.commissionStructure}"`,
          `"${c.createdAt}"`,
          c.shouldTriggerCommission,
          `"${c.exclusionReason || ''}"`,
          c.discrepancies.length
        ].join(','));
      }
      lines.push('');

      lines.push('=== MULTI-LEVEL CONVERSIONS (10% + 5%, Cascading) ===');
      lines.push('Conversion ID,User ID,Username,USDT Amount,Shares Requested,Share Price,Phase,Commission Structure,Date,Should Trigger,Exclusion Reason,Discrepancy Count');
      const multilevelConversions = results.conversionAudit.filter(c => (c.phaseId ?? 0) >= 2);
      for (const c of multilevelConversions) {
        lines.push([
          `"${c.conversionId}"`,
          c.userId,
          `"${c.username}"`,
          c.usdtAmount,
          c.sharesRequested,
          c.sharePrice,
          c.phaseId,
          `"${c.commissionStructure}"`,
          `"${c.createdAt}"`,
          c.shouldTriggerCommission,
          `"${c.exclusionReason || ''}"`,
          c.discrepancies.length
        ].join(','));
      }
      lines.push('');

      // Download
      const blob = new Blob([lines.join('\n')], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `commission-audit-${startDate}-to-${endDate}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="p-6 bg-gray-900 min-h-screen text-white">
      <h1 className="text-2xl font-bold mb-6 text-amber-400">
        🔍 Commission Cascade Audit
      </h1>

      {/* Audit Filters */}
      <div className="bg-gray-800 rounded-lg p-4 mb-6">
        <h2 className="text-lg font-semibold mb-4">Audit Filters</h2>
        <div className="flex flex-wrap gap-4 items-end">
          <div>
            <label className="block text-sm text-white mb-1">Start Date</label>
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="bg-gray-700 border border-gray-600 rounded px-3 py-2 text-white"
            />
          </div>
          <div>
            <label className="block text-sm text-white mb-1">End Date</label>
            <input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="bg-gray-700 border border-gray-600 rounded px-3 py-2 text-white"
            />
          </div>
          <div>
            <label className="block text-sm text-white mb-1">
              User Search <span className="text-gray-500">(optional)</span>
            </label>
            <input
              type="text"
              value={userSearch}
              onChange={(e) => setUserSearch(e.target.value)}
              placeholder="Enter username..."
              className="bg-gray-700 border border-gray-600 rounded px-3 py-2 text-white w-48"
            />
          </div>
          <button
            onClick={runAudit}
            disabled={loading}
            className="bg-amber-600 hover:bg-amber-700 disabled:bg-gray-600 px-6 py-2 rounded font-semibold"
          >
            {loading ? '⏳ Running Audit...' : '▶️ Run Audit'}
          </button>
          {userSearch && (
            <button
              onClick={() => { setUserSearch(''); }}
              className="bg-gray-600 hover:bg-gray-500 px-4 py-2 rounded"
            >
              ✕ Clear User
            </button>
          )}
          {results && (
            <button
              onClick={exportToCSV}
              disabled={exporting}
              className="bg-green-600 hover:bg-green-700 disabled:bg-gray-600 px-4 py-2 rounded"
            >
              {exporting ? '📤 Exporting...' : '📥 Export CSV'}
            </button>
          )}
        </div>
      </div>

      {/* Error Display */}
      {error && (
        <div className="bg-red-900/50 border border-red-500 rounded-lg p-4 mb-6">
          <p className="text-red-300">❌ {error}</p>
        </div>
      )}

      {/* Fix Result Notification with Validation */}
      {fixResult && (
        <div className={`${fixResult.success ? 'bg-green-900/50 border-green-500' : 'bg-red-900/50 border-red-500'} border rounded-lg p-4 mb-6`}>
          <div className="flex justify-between items-start">
            <div className="flex-1">
              <p className={fixResult.success ? 'text-green-300' : 'text-red-300'}>
                {fixResult.success ? '✅' : '❌'} {fixResult.message}
              </p>
              {/* Validation Details */}
              {fixResult.validation && (
                <div className="mt-2 text-sm">
                  <p className={fixResult.validation.valid ? 'text-green-400' : 'text-amber-400'}>
                    🔍 Validation: {fixResult.validation.message}
                  </p>
                  {fixResult.validation.checks && fixResult.validation.checks.length > 0 && (
                    <ul className="mt-1 ml-4 text-sm text-white">
                      {fixResult.validation.checks.map((check, i) => (
                        <li key={i} className={check.passed ? 'text-green-400' : 'text-red-400'}>
                          {check.passed ? '✓' : '✗'} {check.name}: {check.details}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              )}
            </div>
            <button onClick={() => setFixResult(null)} className="text-gray-400 hover:text-white ml-4">✕</button>
          </div>
        </div>
      )}

      {/* Bulk Progress Indicator */}
      {bulkProgress && (
        <div className="bg-blue-900/50 border border-blue-500 rounded-lg p-4 mb-6">
          <div className="flex items-center justify-between mb-2">
            <span className="text-blue-300">
              ⏳ Processing {bulkProgress.current} of {bulkProgress.total}...
            </span>
            {bulkProgress.failures.length > 0 && (
              <span className="text-red-400">
                {bulkProgress.failures.length} failed
              </span>
            )}
          </div>
          <div className="w-full bg-gray-700 rounded-full h-2">
            <div
              className="bg-blue-500 h-2 rounded-full transition-all duration-300"
              style={{ width: `${(bulkProgress.current / bulkProgress.total) * 100}%` }}
            />
          </div>
        </div>
      )}

      {/* Confirmation Modal */}
      {showConfirmModal && confirmModalAction && confirmModalAction.type !== 'single-fix' && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50">
          <div className="bg-gray-800 rounded-lg p-6 max-w-md w-full mx-4 border border-gray-700">
            <h3 className="text-lg font-semibold mb-4 text-amber-400">
              ⚠️ Confirm {confirmModalAction.type === 'bulk-fix' ? 'Bulk Fix' : 'Bulk Ignore'}
            </h3>
            <p className="text-gray-300 mb-6">
              Are you sure you want to {confirmModalAction.type === 'bulk-fix' ? 'fix' : 'ignore'}{' '}
              <span className="font-bold text-white">{confirmModalAction.count}</span> discrepancies?
              {confirmModalAction.type === 'bulk-fix' && (
                <span className="block mt-2 text-amber-300 text-sm">
                  This will create new commission records for the selected items.
                </span>
              )}
              {confirmModalAction.type === 'bulk-ignore' && (
                <span className="block mt-2 text-white text-sm">
                  These items will be marked as accepted and hidden from future audits.
                </span>
              )}
            </p>

            {/* v4.8.21: Notification suppression toggle for bulk back-fills */}
            {confirmModalAction.type === 'bulk-fix' && (
              <label className="flex items-start gap-3 mb-6 p-3 bg-gray-900/50 rounded border border-gray-700 cursor-pointer hover:border-gray-600">
                <input
                  type="checkbox"
                  checked={bulkSuppressNotifications}
                  onChange={(e) => setBulkSuppressNotifications(e.target.checked)}
                  className="mt-1 h-4 w-4 accent-amber-500"
                />
                <span className="text-sm text-gray-200">
                  <span className="font-semibold">Suppress commission emails</span>
                  <span className="block text-xs text-gray-400 mt-1">
                    Recommended for back-filling stale (multi-day-old) cascades. Uncheck to
                    send each affected user a &ldquo;you earned a commission&rdquo; email.
                  </span>
                </span>
              </label>
            )}

            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setShowConfirmModal(false)}
                className="px-4 py-2 bg-gray-600 hover:bg-gray-500 rounded"
              >
                Cancel
              </button>
              <button
                onClick={() => confirmModalAction.onConfirm({ suppressNotifications: bulkSuppressNotifications })}
                className={`px-4 py-2 rounded font-semibold ${
                  confirmModalAction.type === 'bulk-fix'
                    ? 'bg-green-600 hover:bg-green-700'
                    : 'bg-red-600 hover:bg-red-700'
                }`}
              >
                {confirmModalAction.type === 'bulk-fix' ? '🔧 Fix All' : '🙈 Ignore All'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* v4.8.22 #6: Single-row Fix confirmation modal — full payload preview */}
      {showConfirmModal && confirmModalAction && confirmModalAction.type === 'single-fix' && (() => {
        const p = confirmModalAction.params;
        const isWrong = p.fixType === 'WRONG_REFERRER';
        const sharePurchaseValue = (p.expectedShares || 0) * (p.sharePrice || 0);
        return (
          <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50">
            <div className="bg-gray-800 rounded-lg p-6 max-w-2xl w-full mx-4 border border-gray-700 max-h-[90vh] overflow-y-auto">
              <h3 className={`text-lg font-semibold mb-4 ${isWrong ? 'text-red-400' : 'text-amber-400'}`}>
                {isWrong ? '🔄 Confirm Commission Reassignment' : '🔧 Confirm Commission Fix'}
              </h3>
              <p className="text-gray-300 mb-4 text-sm">
                {isWrong
                  ? 'This will deduct the commission from the wrong referrer (logging any shortfall) and credit the correct referrer.'
                  : 'This will create a new approved commission row and credit the referrer\u2019s balance atomically.'}
              </p>

              <div className="bg-gray-900/60 rounded border border-gray-700 p-4 mb-4 text-sm">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-2">
                  <div className="text-gray-400">Fix type</div>
                  <div className="text-white font-mono">{p.fixType || 'MISSING_COMMISSION'}</div>

                  <div className="text-gray-400">Source</div>
                  <div className="text-white font-mono break-all">{p.type} / {p.sourceId || '(none)'}</div>

                  <div className="text-gray-400">Phase</div>
                  <div className="text-white">Phase {p.phaseId}</div>

                  <div className="text-gray-400">Level</div>
                  <div className="text-white">L{p.level}</div>

                  {p.display?.triggeredByUsername && (
                    <>
                      <div className="text-gray-400">Triggered by</div>
                      <div className="text-cyan-300">
                        {p.display.triggeredByUsername}
                        {p.display.triggerAmount ? ` ($${p.display.triggerAmount.toFixed(2)})` : ''}
                      </div>
                    </>
                  )}

                  <div className="text-gray-400">Correct referrer</div>
                  <div className="text-green-300">
                    {p.display?.referrerUsername || '(unknown)'} <span className="text-gray-500">(ID: {p.referrerId})</span>
                  </div>

                  {isWrong && (
                    <>
                      <div className="text-gray-400">Wrong referrer (clawback)</div>
                      <div className="text-red-300">
                        {p.display?.wrongReferrerUsername || '(unknown)'}{' '}
                        <span className="text-gray-500">(ID: {p.wrongReferrerId})</span>
                      </div>
                      <div className="text-gray-400">Existing commission row</div>
                      <div className="text-white font-mono break-all">{p.existingCommissionId || '(none)'}</div>
                    </>
                  )}

                  <div className="text-gray-400">Base amount</div>
                  <div className="text-white">${(p.baseAmount || 0).toFixed(6)}</div>

                  <div className="text-gray-400">Share price</div>
                  <div className="text-white">${(p.sharePrice || 0).toFixed(6)}</div>

                  <div className="text-gray-400">Expected USDT</div>
                  <div className="text-green-300 font-semibold">${(p.expectedUSDT || 0).toFixed(6)}</div>

                  <div className="text-gray-400">Expected shares</div>
                  <div className="text-green-300 font-semibold">{(p.expectedShares || 0).toFixed(6)}</div>

                  <div className="text-gray-400">Share-purchase value</div>
                  <div className="text-white">${sharePurchaseValue.toFixed(6)}</div>

                  {p.display?.createdAt && (
                    <>
                      <div className="text-gray-400">Event date</div>
                      <div className="text-white">{p.display.createdAt}</div>
                    </>
                  )}
                </div>
              </div>

              {(!p.sourceId || (p.expectedUSDT === 0 && p.expectedShares === 0)) && (
                <div className="mb-4 p-3 bg-red-900/40 border border-red-600 rounded text-sm text-red-200">
                  ⚠️ Backend will reject this payload (missing source ID or 0/0 payout). Resolve before confirming.
                </div>
              )}

              <div className="flex gap-3 justify-end">
                <button
                  onClick={() => setShowConfirmModal(false)}
                  className="px-4 py-2 bg-gray-600 hover:bg-gray-500 rounded"
                >
                  Cancel
                </button>
                <button
                  onClick={() => confirmModalAction.onConfirm()}
                  className={`px-4 py-2 rounded font-semibold ${
                    isWrong ? 'bg-red-600 hover:bg-red-700' : 'bg-green-600 hover:bg-green-700'
                  }`}
                >
                  {isWrong ? '🔄 Confirm Reassign' : '🔧 Confirm Fix'}
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Controls Bar (Auto-refresh, Undo) */}
      {results && (
        <div className="flex items-center justify-between bg-gray-800 rounded-lg p-3 mb-4">
          {/* Auto-refresh */}
          <div className="flex items-center gap-3">
            <span className="text-sm text-white">Auto-refresh:</span>
            <select
              value={autoRefreshInterval}
              onChange={(e) => setAutoRefreshInterval(Number(e.target.value))}
              className="bg-gray-700 border border-gray-600 rounded px-2 py-1 text-sm"
            >
              {AUTO_REFRESH_INTERVALS.map(opt => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
            {autoRefreshInterval > 0 && (
              <span className="text-green-400 text-sm animate-pulse">● Active</span>
            )}
          </div>

          {/* Undo Button */}
          <div className="flex items-center gap-3">
            {undoStack.length > 0 && (
              <button
                onClick={handleUndo}
                disabled={undoing}
                className="bg-gray-600 hover:bg-gray-500 disabled:bg-gray-700 px-3 py-1 rounded text-sm flex items-center gap-1"
              >
                {undoing ? '⏳' : '↩️'} Undo Last ({undoStack.length})
              </button>
            )}
          </div>
        </div>
      )}

      {/* Results */}
      {results && (
        <>
          {/* User Filter Banner */}
          {results.filteredUser && (
            <div className="bg-blue-900/50 border border-blue-500 rounded-lg p-4 mb-4 flex items-center justify-between">
              <div>
                <p className="text-blue-300 font-semibold">
                  👤 Auditing User: <span className="text-white">{results.filteredUser.username}</span>
                </p>
                <p className="text-sm text-blue-400">
                  User ID: {results.filteredUser.id} | Email: {results.filteredUser.email}
                </p>
              </div>
              <button
                onClick={() => { setUserSearch(''); runAudit(); }}
                className="bg-blue-600 hover:bg-blue-700 px-4 py-2 rounded text-sm"
              >
                Show All Users
              </button>
            </div>
          )}

          {/* Tab Navigation */}
          <div className="flex gap-2 mb-4 border-b border-gray-700 pb-2 flex-wrap">
            {(['summary', 'purchases', 'presale-conversions', 'multilevel-conversions', 'purchase-issues', 'conversion-issues'] as const).map((tab) => {
              const purchaseIssues = results.discrepancies.filter(d => d.sourceType === 'purchase');
              const conversionIssues = results.discrepancies.filter(d => d.sourceType === 'conversion');
              const presaleConversionIssues = results.discrepancies.filter(d => d.sourceType === 'conversion' && d.phaseId === 1);
              const multilevelConversionIssues = results.discrepancies.filter(d => d.sourceType === 'conversion' && d.phaseId && d.phaseId >= 2);

              const tabLabels: Record<string, string> = {
                'summary': 'Summary',
                'purchases': 'Purchases',
                'presale-conversions': 'Pre-Sale Conversions',
                'multilevel-conversions': 'Multi-Level Conversions',
                'purchase-issues': 'Purchase Issues',
                'conversion-issues': 'Conversion Issues'
              };

              const issueCount = tab === 'purchase-issues' ? purchaseIssues.length
                               : tab === 'conversion-issues' ? conversionIssues.length
                               : tab === 'presale-conversions' ? presaleConversionIssues.length
                               : tab === 'multilevel-conversions' ? multilevelConversionIssues.length
                               : 0;

              return (
                <button
                  key={tab}
                  onClick={() => setActiveTab(tab)}
                  className={`px-4 py-2 rounded-t ${
                    activeTab === tab
                      ? 'bg-amber-600 text-white'
                      : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                  }`}
                >
                  {tabLabels[tab]}
                  {issueCount > 0 && (
                    <span className="ml-2 bg-red-500 text-white text-sm px-2 py-0.5 rounded-full">
                      {issueCount}
                    </span>
                  )}
                </button>
              );
            })}
          </div>

          {/* Summary Tab */}
          {activeTab === 'summary' && <SummaryTab results={results} />}

          {/* Purchases Tab */}
          {activeTab === 'purchases' && (
            <PurchasesTab
              purchases={results.purchaseDetails}
              expandedId={expandedPurchaseId}
              setExpandedId={setExpandedPurchaseId}
              filterDiscrepanciesOnly={filterDiscrepanciesOnly}
              setFilterDiscrepanciesOnly={setFilterDiscrepanciesOnly}
              onFixCommission={handleFixCommission}
              fixingCommission={fixingCommission}
            />
          )}

          {/* Pre-Sale Conversions Tab */}
          {activeTab === 'presale-conversions' && (
            <DiscrepanciesTab
              discrepancies={results.discrepancies.filter(d => d.sourceType === 'conversion' && d.phaseId === 1)}
              title="Pre-Sale Conversion Issues (15% USDT + 15% Shares)"
              onFixCommission={handleFixCommission}
              fixingCommission={fixingCommission}
              onIgnoreDiscrepancy={handleIgnoreDiscrepancy}
              ignoringDiscrepancy={ignoringDiscrepancy}
              selectedForFix={selectedForFix}
              setSelectedForFix={setSelectedForFix}
              toggleSelection={toggleSelection}
              onBulkFix={handleBulkFix}
              bulkFixing={bulkFixing}
              onBulkIgnore={handleBulkIgnore}
              bulkIgnoring={bulkIgnoring}
            />
          )}

          {/* Multi-Level Conversions Tab */}
          {activeTab === 'multilevel-conversions' && (
            <DiscrepanciesTab
              discrepancies={results.discrepancies.filter(d => d.sourceType === 'conversion' && d.phaseId && d.phaseId >= 2)}
              title="Multi-Level Conversion Issues (10% USDT + 5% Shares)"
              onFixCommission={handleFixCommission}
              fixingCommission={fixingCommission}
              onIgnoreDiscrepancy={handleIgnoreDiscrepancy}
              ignoringDiscrepancy={ignoringDiscrepancy}
              selectedForFix={selectedForFix}
              setSelectedForFix={setSelectedForFix}
              toggleSelection={toggleSelection}
              onBulkFix={handleBulkFix}
              bulkFixing={bulkFixing}
              onBulkIgnore={handleBulkIgnore}
              bulkIgnoring={bulkIgnoring}
            />
          )}

          {/* Purchase Issues Tab */}
          {activeTab === 'purchase-issues' && (
            <DiscrepanciesTab
              discrepancies={results.discrepancies.filter(d => d.sourceType === 'purchase')}
              title="Purchase Commission Issues"
              onFixCommission={handleFixCommission}
              fixingCommission={fixingCommission}
              onIgnoreDiscrepancy={handleIgnoreDiscrepancy}
              ignoringDiscrepancy={ignoringDiscrepancy}
              selectedForFix={selectedForFix}
              setSelectedForFix={setSelectedForFix}
              toggleSelection={toggleSelection}
              onBulkFix={handleBulkFix}
              bulkFixing={bulkFixing}
              onBulkIgnore={handleBulkIgnore}
              bulkIgnoring={bulkIgnoring}
            />
          )}

          {/* Conversion Issues Tab */}
          {activeTab === 'conversion-issues' && (
            <DiscrepanciesTab
              discrepancies={results.discrepancies.filter(d => d.sourceType === 'conversion')}
              title="Conversion Commission Issues"
              onFixCommission={handleFixCommission}
              fixingCommission={fixingCommission}
              onIgnoreDiscrepancy={handleIgnoreDiscrepancy}
              ignoringDiscrepancy={ignoringDiscrepancy}
              selectedForFix={selectedForFix}
              setSelectedForFix={setSelectedForFix}
              toggleSelection={toggleSelection}
              onBulkFix={handleBulkFix}
              bulkFixing={bulkFixing}
              onBulkIgnore={handleBulkIgnore}
              bulkIgnoring={bulkIgnoring}
            />
          )}
        </>
      )}
    </div>
  );
}

// Summary Tab Component
function SummaryTab({ results }: { results: AuditResults }) {
  const { summary } = results;
  const hasIssues = summary.missingCommissionCount > 0 ||
                    summary.incorrectUSDTCount > 0 ||
                    summary.incorrectShareCount > 0;

  return (
    <div className="bg-gray-800 rounded-lg p-6">
      <h2 className="text-xl font-semibold mb-4">
        {hasIssues ? '⚠️ Audit Summary - Issues Found' : '✅ Audit Summary - All Clear'}
      </h2>

      {/* Overview Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <StatCard label="Total Audited" value={results.totalTransactionsAudited} color="blue" />
        <StatCard label="With Commissions" value={results.transactionsWithCommissions} color="green" />
        <StatCard label="Excluded" value={results.excludedTransactions} color="gray" />
        <StatCard
          label="Discrepancies"
          value={results.discrepancies.length}
          color={results.discrepancies.length > 0 ? 'red' : 'green'}
        />
      </div>

      {/* Issue Breakdown */}
      {hasIssues && (
        <div className="bg-red-900/30 border border-red-500/50 rounded-lg p-4 mb-6">
          <h3 className="text-lg font-semibold text-red-300 mb-3">Issue Breakdown</h3>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            <div>
              <p className="text-white text-sm">Missing Commissions</p>
              <p className="text-2xl font-bold text-red-400">{summary.missingCommissionCount}</p>
            </div>
            <div>
              <p className="text-white text-sm">Total Missing USDT</p>
              <p className="text-2xl font-bold text-red-400">${summary.totalMissingUSDT.toFixed(4)}</p>
            </div>
            <div>
              <p className="text-white text-sm">Total Missing Shares</p>
              <p className="text-2xl font-bold text-red-400">{summary.totalMissingShares.toFixed(4)}</p>
            </div>
            <div>
              <p className="text-white text-sm">Incorrect USDT</p>
              <p className="text-2xl font-bold text-amber-400">{summary.incorrectUSDTCount}</p>
            </div>
            <div>
              <p className="text-white text-sm">Incorrect Shares</p>
              <p className="text-2xl font-bold text-amber-400">{summary.incorrectShareCount}</p>
            </div>
            <div>
              <p className="text-white text-sm">Wrong Pricing</p>
              <p className="text-2xl font-bold text-amber-400">{summary.wrongPricingCount}</p>
            </div>
          </div>
        </div>
      )}

      {/* Conversion Audit Summary - Split by Phase */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Pre-Sale Conversions */}
        <div className="bg-blue-900/30 border border-blue-500/50 rounded-lg p-4">
          <h3 className="text-lg font-semibold text-blue-300 mb-3">
            Pre-Sale Conversions (15% + 15%)
          </h3>
          <p className="text-white text-sm mb-2">
            Pre-Sale conversions should trigger 15% USDT + 15% shares to direct sponsor only.
          </p>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <p className="text-white text-sm">Total</p>
              <p className="text-2xl font-bold text-blue-400">
                {results.conversionAudit.filter(c => c.phaseId === 1).length}
              </p>
            </div>
            <div>
              <p className="text-white text-sm">With Issues</p>
              <p className="text-2xl font-bold text-red-400">
                {results.conversionAudit.filter(c => c.phaseId === 1 && c.discrepancies.length > 0).length}
              </p>
            </div>
          </div>
        </div>

        {/* Multi-Level Conversions */}
        <div className="bg-purple-900/30 border border-purple-500/50 rounded-lg p-4">
          <h3 className="text-lg font-semibold text-purple-300 mb-3">
            Multi-Level Conversions (10% + 5%)
          </h3>
          <p className="text-white text-sm mb-2">
            Phase 1+ conversions should trigger cascading multi-level commissions.
          </p>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <p className="text-white text-sm">Total</p>
              <p className="text-2xl font-bold text-purple-400">
                {results.conversionAudit.filter(c => (c.phaseId ?? 0) >= 2).length}
              </p>
            </div>
            <div>
              <p className="text-white text-sm">With Issues</p>
              <p className="text-2xl font-bold text-red-400">
                {results.conversionAudit.filter(c => (c.phaseId ?? 0) >= 2 && c.discrepancies.length > 0).length}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Audit Errors (if any) */}
      {results.auditErrors && results.auditErrors.length > 0 && (
        <div className="bg-orange-900/30 border border-orange-500/50 rounded-lg p-4 mt-6">
          <h3 className="text-lg font-semibold text-orange-300 mb-3">
            ⚠️ Audit Errors ({results.auditErrors.length})
          </h3>
          <p className="text-white mb-2">
            Some transactions could not be audited due to errors:
          </p>
          <div className="max-h-40 overflow-y-auto">
            {results.auditErrors.map((err, i) => (
              <div key={i} className="text-sm text-orange-200 py-1 border-b border-orange-700/30">
                {err.purchaseId && <span>Purchase {err.purchaseId}: </span>}
                {err.conversionId && <span>Conversion {err.conversionId}: </span>}
                {err.error}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// Stat Card Component
function StatCard({ label, value, color }: { label: string; value: number; color: string }) {
  const colorClasses: Record<string, string> = {
    blue: 'bg-blue-900/50 border-blue-500/50 text-blue-300',
    green: 'bg-green-900/50 border-green-500/50 text-green-300',
    red: 'bg-red-900/50 border-red-500/50 text-red-300',
    gray: 'bg-gray-700/50 border-gray-500/50 text-gray-300',
    amber: 'bg-amber-900/50 border-amber-500/50 text-amber-300'
  };

  return (
    <div className={`rounded-lg p-4 border ${colorClasses[color] || colorClasses.gray}`}>
      <p className="text-sm text-white">{label}</p>
      <p className="text-2xl font-bold">{value}</p>
    </div>
  );
}

// Fix handler type
// v4.8.22 #6: optional `display` block is purely for the per-row confirmation modal
// preview; it is stripped before POSTing to /api/admin/fix-missing-commission.
type FixCommissionParams = {
  type: 'purchase' | 'conversion';
  sourceId: string;
  referredId: number;
  referrerId: number;
  level: number;
  expectedUSDT: number;
  expectedShares: number;
  baseAmount: number;
  phaseId: number;
  sharePrice: number;
  // Optional fields for WRONG_REFERRER fixes
  fixType?: 'MISSING_COMMISSION' | 'WRONG_REFERRER';
  wrongReferrerId?: number;
  existingCommissionId?: string;
  // Discrepancy type for auto-ignore after fix
  discrepancyType?: string;
  // UI-only payload preview (not sent to backend)
  display?: {
    referrerUsername?: string;
    wrongReferrerUsername?: string;
    triggeredByUsername?: string;
    triggerAmount?: number;
    createdAt?: string;
  };
};
type FixCommissionHandler = (params: FixCommissionParams) => Promise<void>;

// Purchases Tab Component with pagination
function PurchasesTab({
  purchases,
  expandedId,
  setExpandedId,
  filterDiscrepanciesOnly,
  setFilterDiscrepanciesOnly,
  onFixCommission,
  fixingCommission
}: {
  purchases: PurchaseDetail[];
  expandedId: string | null;
  setExpandedId: (id: string | null) => void;
  filterDiscrepanciesOnly: boolean;
  setFilterDiscrepanciesOnly: (v: boolean) => void;
  onFixCommission: FixCommissionHandler;
  fixingCommission: string | null;
}) {
  const [currentPage, setCurrentPage] = React.useState(1);
  const pageSize = 50;

  const filtered = filterDiscrepanciesOnly
    ? purchases.filter(p => p.discrepancies.length > 0)
    : purchases;

  const totalPages = Math.ceil(filtered.length / pageSize);
  const paginatedPurchases = filtered.slice((currentPage - 1) * pageSize, currentPage * pageSize);

  // Reset to page 1 when filter changes
  React.useEffect(() => {
    setCurrentPage(1);
  }, [filterDiscrepanciesOnly]);

  return (
    <div className="bg-gray-800 rounded-lg p-4">
      <div className="flex flex-wrap justify-between items-center mb-4 gap-2">
        <h2 className="text-lg font-semibold">Purchase Audit Details ({filtered.length})</h2>
        <div className="flex items-center gap-4">
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={filterDiscrepanciesOnly}
              onChange={(e) => setFilterDiscrepanciesOnly(e.target.checked)}
              className="rounded"
            />
            Show only with discrepancies
          </label>
        </div>
      </div>

      {/* Pagination Controls - Top */}
      {totalPages > 1 && (
        <div className="flex justify-between items-center mb-4 text-sm">
          <span className="text-white">
            Showing {(currentPage - 1) * pageSize + 1}-{Math.min(currentPage * pageSize, filtered.length)} of {filtered.length}
          </span>
          <div className="flex gap-2">
            <button
              onClick={() => setCurrentPage(1)}
              disabled={currentPage === 1}
              className="px-2 py-1 bg-gray-700 rounded disabled:opacity-50"
            >
              ⏮
            </button>
            <button
              onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
              disabled={currentPage === 1}
              className="px-2 py-1 bg-gray-700 rounded disabled:opacity-50"
            >
              ◀
            </button>
            <span className="px-3 py-1">Page {currentPage} of {totalPages}</span>
            <button
              onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
              disabled={currentPage === totalPages}
              className="px-2 py-1 bg-gray-700 rounded disabled:opacity-50"
            >
              ▶
            </button>
            <button
              onClick={() => setCurrentPage(totalPages)}
              disabled={currentPage === totalPages}
              className="px-2 py-1 bg-gray-700 rounded disabled:opacity-50"
            >
              ⏭
            </button>
          </div>
        </div>
      )}

      <div className="space-y-2 max-h-[600px] overflow-y-auto">
        {paginatedPurchases.map((purchase) => (
          <PurchaseRow
            key={purchase.purchaseId}
            purchase={purchase}
            isExpanded={expandedId === purchase.purchaseId}
            onToggle={() => setExpandedId(expandedId === purchase.purchaseId ? null : purchase.purchaseId)}
            onFixCommission={onFixCommission}
            fixingCommission={fixingCommission}
          />
        ))}
        {filtered.length === 0 && (
          <p className="text-white text-center py-8">No purchases to display</p>
        )}
      </div>
    </div>
  );
}

// Purchase Row Component
function PurchaseRow({
  purchase,
  isExpanded,
  onToggle,
  onFixCommission,
  fixingCommission
}: {
  purchase: PurchaseDetail;
  isExpanded: boolean;
  onToggle: () => void;
  onFixCommission: FixCommissionHandler;
  fixingCommission: string | null;
}) {
  const hasDiscrepancies = purchase.discrepancies.length > 0;
  const statusColor = !purchase.shouldTriggerCommission
    ? 'bg-gray-700'
    : hasDiscrepancies
      ? 'bg-red-900/30 border-red-500/50'
      : 'bg-green-900/30 border-green-500/50';

  return (
    <div className={`rounded-lg border ${statusColor}`}>
      {/* Header Row */}
      <div
        className="p-3 cursor-pointer flex justify-between items-center"
        onClick={onToggle}
      >
        <div className="flex items-center gap-4">
          <span className="text-lg">{isExpanded ? '▼' : '▶'}</span>
          <div>
            <p className="font-semibold">{purchase.username}</p>
            <p className="text-sm text-white">
              ${purchase.amount.toFixed(2)} • {purchase.shares.toFixed(4)} shares • {purchase.phaseName}
            </p>
          </div>
        </div>
        <div className="text-right">
          <p className="text-sm text-white">{new Date(purchase.createdAt).toLocaleDateString()}</p>
          {!purchase.shouldTriggerCommission && (
            <span className="text-sm bg-gray-600 px-2 py-0.5 rounded">
              {purchase.exclusionReason}
            </span>
          )}
          {hasDiscrepancies && (
            <span className="text-sm bg-red-600 px-2 py-0.5 rounded ml-2">
              {purchase.discrepancies.length} issue(s)
            </span>
          )}
        </div>
      </div>

      {/* Expanded Details */}
      {isExpanded && (
        <div className="border-t border-gray-700 p-4">
          <div className="grid grid-cols-2 gap-4 mb-4 text-sm">
            <div>
              <p className="text-white">Purchase ID</p>
              <p className="font-mono text-sm">{purchase.purchaseId}</p>
            </div>
            <div>
              <p className="text-white">Payment Method</p>
              <p>{purchase.paymentMethod}</p>
            </div>
            <div>
              <p className="text-white">Price Per Share</p>
              <p>${purchase.pricePerShare}</p>
              {purchase.priceSource && (
                <p className="text-sm text-gray-500">Source: {purchase.priceSource}</p>
              )}
            </div>
            <div>
              <p className="text-white">User ID</p>
              <p>{purchase.userId}</p>
            </div>
          </div>

          {/* Expected vs Actual Commissions */}
          {purchase.shouldTriggerCommission && (
            <CommissionComparisonTable
              expected={purchase.expectedCommissions}
              actual={purchase.actualCommissions}
              discrepancies={purchase.discrepancies}
              sourceType="purchase"
              sourceId={purchase.purchaseId}
              referredId={purchase.userId}
              phaseId={purchase.phaseId}
              sharePrice={purchase.pricePerShare}
              onFixCommission={onFixCommission}
              fixingCommission={fixingCommission}
            />
          )}
        </div>
      )}
    </div>
  );
}


// Commission Comparison Table
function CommissionComparisonTable({
  expected,
  actual,
  discrepancies,
  sourceType,
  sourceId,
  referredId,
  phaseId,
  sharePrice,
  onFixCommission,
  fixingCommission
}: {
  expected: ExpectedCommission[];
  actual: ActualCommission[];
  discrepancies: Discrepancy[];
  sourceType: 'purchase' | 'conversion';
  sourceId: string;
  referredId: number;
  phaseId: number;
  sharePrice: number;
  onFixCommission: FixCommissionHandler;
  fixingCommission: string | null;
}) {
  if (expected.length === 0) {
    return <p className="text-white text-sm">No commissions expected (no referral chain)</p>;
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="text-left text-white border-b border-gray-700">
            <th className="py-2 px-2">Level</th>
            <th className="py-2 px-2">Referrer</th>
            <th className="py-2 px-2">Base Amount</th>
            <th className="py-2 px-2">Expected USDT</th>
            <th className="py-2 px-2">Actual USDT</th>
            <th className="py-2 px-2">Expected Shares</th>
            <th className="py-2 px-2">Actual Shares</th>
            <th className="py-2 px-2">Status</th>
            <th className="py-2 px-2">Action</th>
          </tr>
        </thead>
        <tbody>
          {expected.map((exp) => {
            const act = actual.find(a => a.level === exp.level && a.referrerId === exp.referrerId);
            const disc = discrepancies.filter(d => d.level === exp.level && d.referrerId === exp.referrerId);
            const hasIssue = disc.length > 0;
            // Check for both purchase and conversion missing commission types
            // Also treat WRONG_REFERRER as fixable (needs reassignment)
            const isMissing = disc.some(d =>
              d.type === 'MISSING_COMMISSION' || d.type === 'MISSING_CONVERSION_COMMISSION' || d.type === 'WRONG_REFERRER'
            );
            const fixKey = `${sourceType}-${sourceId}-L${exp.level}-${exp.referrerId}`;
            const isFixing = fixingCommission === fixKey;

            return (
              <tr
                key={`${exp.level}-${exp.referrerId}`}
                className={hasIssue ? 'bg-red-900/20' : 'bg-green-900/10'}
              >
                <td className="py-2 px-2">L{exp.level}</td>
                <td className="py-2 px-2">{exp.referrerUsername}</td>
                <td className="py-2 px-2">${exp.baseAmount.toFixed(4)}</td>
                <td className="py-2 px-2">${exp.expectedUSDT.toFixed(6)}</td>
                <td className="py-2 px-2">
                  {act ? `$${act.actualUSDT.toFixed(6)}` : <span className="text-red-400">MISSING</span>}
                </td>
                <td className="py-2 px-2">{exp.expectedShares.toFixed(6)}</td>
                <td className="py-2 px-2">
                  {act ? act.actualShares.toFixed(6) : <span className="text-red-400">MISSING</span>}
                </td>
                <td className="py-2 px-2">
                  {hasIssue ? (
                    <span className="text-red-400">❌ {disc.map(d => d.type).join(', ')}</span>
                  ) : (
                    <span className="text-green-400">✅</span>
                  )}
                </td>
                <td className="py-2 px-2">
                  {isMissing && (
                    <button
                      onClick={() => onFixCommission({
                        type: sourceType,
                        sourceId,
                        referredId,
                        referrerId: exp.referrerId,
                        level: exp.level,
                        expectedUSDT: exp.expectedUSDT,
                        expectedShares: exp.expectedShares,
                        baseAmount: exp.baseAmount,
                        phaseId,
                        sharePrice,
                        // v4.8.22 #6: UI-only preview block for the confirmation modal
                        display: {
                          referrerUsername: exp.referrerUsername
                        }
                      })}
                      disabled={isFixing}
                      className="bg-amber-600 hover:bg-amber-700 disabled:bg-gray-600 px-2 py-1 rounded text-sm font-semibold"
                    >
                      {isFixing ? '⏳ Fixing...' : '🔧 Fix'}
                    </button>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

// Conversions Tab Component
function __ConversionsTab({
  conversions,
  expandedId,
  setExpandedId,
  onFixCommission,
  fixingCommission,
  title,
  description
}: {
  conversions: ConversionAudit[];
  expandedId: string | null;
  setExpandedId: (id: string | null) => void;
  onFixCommission: FixCommissionHandler;
  fixingCommission: string | null;
  title?: string;
  description?: string;
}) {
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 50;

  const __withIssues = conversions.filter(c => c.discrepancies.length > 0);

  // Pagination calculations
  const totalPages = Math.ceil(conversions.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = Math.min(startIndex + itemsPerPage, conversions.length);
  const paginatedConversions = conversions.slice(startIndex, endIndex);

  return (
    <div className="bg-gray-800 rounded-lg p-4">
      <div className="mb-4">
        <h2 className="text-lg font-semibold">{title || 'USDT-to-Shares Conversion Audit'}</h2>
        <p className="text-sm text-white">
          {description || '⚠️ Conversions should trigger commissions but may not be properly linked.'}
        </p>
        {conversions.length > 0 && (
          <p className="text-sm text-red-400 mt-1 font-semibold">
            ⚠️ Showing {conversions.length} conversion{conversions.length !== 1 ? 's' : ''} with missing or incorrect commissions
          </p>
        )}
      </div>

      <div className="space-y-2 max-h-[600px] overflow-y-auto">
        {paginatedConversions.map((conv) => (
          <ConversionRow
            key={conv.conversionId}
            conversion={conv}
            isExpanded={expandedId === conv.conversionId}
            onToggle={() => setExpandedId(expandedId === conv.conversionId ? null : conv.conversionId)}
            onFixCommission={onFixCommission}
            fixingCommission={fixingCommission}
          />
        ))}
        {conversions.length === 0 && (
          <div className="text-center py-12">
            <p className="text-green-400 text-lg font-semibold mb-2">✅ No Issues Found!</p>
            <p className="text-white">All conversions in this category have correct commissions.</p>
          </div>
        )}
      </div>

      {/* Pagination Controls */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between mt-4 pt-4 border-t border-gray-700">
          <p className="text-sm text-white">
            Showing {startIndex + 1}-{endIndex} of {conversions.length}
          </p>
          <div className="flex gap-2">
            <button
              onClick={() => setCurrentPage(1)}
              disabled={currentPage === 1}
              className="px-3 py-1 bg-gray-700 rounded disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-600"
            >
              First
            </button>
            <button
              onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
              disabled={currentPage === 1}
              className="px-3 py-1 bg-gray-700 rounded disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-600"
            >
              Previous
            </button>
            <span className="px-3 py-1 text-white">
              Page {currentPage} of {totalPages}
            </span>
            <button
              onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
              disabled={currentPage === totalPages}
              className="px-3 py-1 bg-gray-700 rounded disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-600"
            >
              Next
            </button>
            <button
              onClick={() => setCurrentPage(totalPages)}
              disabled={currentPage === totalPages}
              className="px-3 py-1 bg-gray-700 rounded disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-600"
            >
              Last
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// Conversion Row Component
function ConversionRow({
  conversion,
  isExpanded,
  onToggle,
  onFixCommission,
  fixingCommission
}: {
  conversion: ConversionAudit;
  isExpanded: boolean;
  onToggle: () => void;
  onFixCommission: FixCommissionHandler;
  fixingCommission: string | null;
}) {
  const hasDiscrepancies = conversion.discrepancies.length > 0;
  const isExcluded = !conversion.shouldTriggerCommission;

  // Status color: gray for excluded, red for discrepancies, green for OK
  const statusColor = isExcluded
    ? 'bg-gray-800/50 border-gray-600/50'
    : hasDiscrepancies
      ? 'bg-red-900/30 border-red-500/50'
      : 'bg-green-900/30 border-green-500/50';

  return (
    <div className={`rounded-lg border ${statusColor}`}>
      <div
        className="p-3 cursor-pointer flex justify-between items-center"
        onClick={onToggle}
      >
        <div className="flex items-center gap-4">
          <span className="text-lg">{isExpanded ? '▼' : '▶'}</span>
          <div>
            <p className="font-semibold">{conversion.username}</p>
            <p className="text-sm text-white">
              ${conversion.usdtAmount.toFixed(2)} → {conversion.sharesRequested.toFixed(4)} shares @ ${conversion.sharePrice}
            </p>
            {/* Display exclusion reason if conversion is excluded */}
            {isExcluded && conversion.exclusionReason && (
              <p className="text-sm text-gray-500 italic mt-1">
                ⚠️ {conversion.exclusionReason}
              </p>
            )}
          </div>
        </div>
        <div className="text-right">
          <p className="text-sm text-white">{new Date(conversion.createdAt).toLocaleDateString()}</p>
          {isExcluded ? (
            <span className="text-sm bg-gray-600 px-2 py-0.5 rounded">
              Excluded
            </span>
          ) : hasDiscrepancies ? (
            <span className="text-sm bg-red-600 px-2 py-0.5 rounded">
              {conversion.discrepancies.length} issue(s)
            </span>
          ) : (
            <span className="text-sm bg-green-600 px-2 py-0.5 rounded">
              OK
            </span>
          )}
        </div>
      </div>

      {isExpanded && (
        <div className="border-t border-gray-700 p-4">
          {isExcluded ? (
            <p className="text-white text-center py-4">
              This conversion is excluded from commission triggering: {conversion.exclusionReason || 'Unknown reason'}
            </p>
          ) : (
            <>
              {/* Enhanced Diagnostic Info */}
              {conversion.diagnostics && (
                <div className="mb-4 p-3 bg-gray-900/50 rounded-lg border border-gray-700">
                  <h4 className="text-sm font-semibold text-amber-400 mb-2">🔍 Diagnostic Info</h4>
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    <div>
                      <span className="text-white">Approval Date:</span>{' '}
                      <span className="text-white">{conversion.diagnostics.approvalDate}</span>
                    </div>
                    <div>
                      <span className="text-white">Matching Attempts:</span>{' '}
                      <span className="text-white">{conversion.diagnostics.matchingAttempts}</span>
                    </div>
                    {conversion.diagnostics.failureReason && (
                      <div className="col-span-2">
                        <span className="text-white">Failure Reason:</span>{' '}
                        <span className="text-red-400">{conversion.diagnostics.failureReason}</span>
                      </div>
                    )}
                    {conversion.diagnostics.possibleMatches.length > 0 && (
                      <div className="col-span-2">
                        <span className="text-white">Possible Matches:</span>{' '}
                        <span className="text-yellow-400">
                          {conversion.diagnostics.possibleMatches.map(m =>
                            `L${m.level} (${m.strategy}${m.timeDiffHours ? `, ${m.timeDiffHours}h delay` : ''})`
                          ).join(', ')}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              )}
              <CommissionComparisonTable
                expected={conversion.expectedCommissions}
                actual={conversion.actualCommissions}
                discrepancies={conversion.discrepancies}
                sourceType="conversion"
                sourceId={conversion.conversionId}
                referredId={conversion.userId}
                phaseId={2} // Conversions are Phase 2+
                sharePrice={conversion.sharePrice}
                onFixCommission={onFixCommission}
                fixingCommission={fixingCommission}
              />
            </>
          )}
        </div>
      )}
    </div>
  );
}

// Discrepancies Tab Component with Sub-Tabs, Filtering, Sorting, Pagination
function DiscrepanciesTab({
  discrepancies,
  title,
  onFixCommission,
  fixingCommission,
  onIgnoreDiscrepancy,
  ignoringDiscrepancy,
  selectedForFix,
  setSelectedForFix,
  toggleSelection,
  onBulkFix,
  bulkFixing,
  onBulkIgnore,
  bulkIgnoring
}: {
  discrepancies: Discrepancy[];
  title?: string;
  onFixCommission?: FixCommissionHandler;
  fixingCommission?: string | null;
  onIgnoreDiscrepancy?: (discrepancy: Discrepancy) => Promise<void>;
  ignoringDiscrepancy?: string | null;
  selectedForFix?: Set<string>;
  setSelectedForFix?: React.Dispatch<React.SetStateAction<Set<string>>>;
  toggleSelection?: (key: string, discrepancy: Discrepancy) => void;
  onBulkFix?: (discrepancies: Discrepancy[]) => Promise<void>;
  bulkFixing?: boolean;
  onBulkIgnore?: (discrepancies: Discrepancy[]) => Promise<void>;
  bulkIgnoring?: boolean;
}) {
  const selectedCount = selectedForFix?.size || 0;

  // Filter state
  const [filterUsername, setFilterUsername] = useState('');
  const [filterLevel, setFilterLevel] = useState<number | null>(null);
  const [filterMinAmount, setFilterMinAmount] = useState<number | null>(null);
  const [quickFilter, setQuickFilter] = useState<'all' | 'today' | 'l1-only' | 'high-value'>('all');

  // Sort state
  const [sortColumn, setSortColumn] = useState<'date' | 'level' | 'amount' | 'referrer' | null>(null);
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');

  // Pagination state
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [currentPage, setCurrentPage] = useState(1);

  // Group by type
  const grouped = discrepancies.reduce((acc, d) => {
    if (!acc[d.type]) acc[d.type] = [];
    acc[d.type].push(d);
    return acc;
  }, {} as Record<string, Discrepancy[]>);

  const discrepancyTypes = Object.keys(grouped);
  const [activeSubTab, setActiveSubTab] = useState<string>(discrepancyTypes[0] || 'ALL');

  // Reset when discrepancies change
  useEffect(() => {
    if (discrepancyTypes.length > 0 && !discrepancyTypes.includes(activeSubTab)) {
      setActiveSubTab(discrepancyTypes[0]);
    }
    setCurrentPage(1);
  }, [discrepancyTypes, activeSubTab]);

  // Apply filters
  const getFilteredItems = (items: Discrepancy[]) => {
    let filtered = [...items];

    // Username filter
    if (filterUsername) {
      const search = filterUsername.toLowerCase();
      filtered = filtered.filter(d =>
        d.referrerUsername?.toLowerCase().includes(search) ||
        d.triggeredByUsername?.toLowerCase().includes(search)
      );
    }

    // Level filter
    if (filterLevel !== null) {
      filtered = filtered.filter(d => d.level === filterLevel);
    }

    // Min amount filter
    if (filterMinAmount !== null) {
      filtered = filtered.filter(d => (d.expectedUSDT || 0) >= filterMinAmount);
    }

    // Quick filters
    if (quickFilter === 'today') {
      const today = new Date().toISOString().split('T')[0];
      filtered = filtered.filter(d => d.createdAt?.startsWith(today));
    } else if (quickFilter === 'l1-only') {
      filtered = filtered.filter(d => d.level === 1);
    } else if (quickFilter === 'high-value') {
      filtered = filtered.filter(d => (d.expectedUSDT || 0) >= 10);
    }

    return filtered;
  };

  // Apply sorting
  const getSortedItems = (items: Discrepancy[]) => {
    if (!sortColumn) return items;

    return [...items].sort((a, b) => {
      let cmp = 0;
      switch (sortColumn) {
        case 'date':
          cmp = (a.createdAt || '').localeCompare(b.createdAt || '');
          break;
        case 'level':
          cmp = a.level - b.level;
          break;
        case 'amount':
          cmp = (a.expectedUSDT || 0) - (b.expectedUSDT || 0);
          break;
        case 'referrer':
          cmp = (a.referrerUsername || '').localeCompare(b.referrerUsername || '');
          break;
      }
      return sortDirection === 'asc' ? cmp : -cmp;
    });
  };

  // Handle sort click
  const handleSort = (column: 'date' | 'level' | 'amount' | 'referrer') => {
    if (sortColumn === column) {
      setSortDirection(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortDirection('desc');
    }
  };

  // Get display name for type
  const getTypeDisplayName = (type: string) => {
    const names: Record<string, string> = {
      'MISSING_COMMISSION': '❌ Missing',
      'MISSING_CONVERSION_COMMISSION': '❌ Missing',
      'INCORRECT_USDT': '⚠️ Incorrect USDT',
      'INCORRECT_SHARES': '⚠️ Incorrect Shares',
      'INCORRECT_CONVERSION_USDT': '⚠️ Incorrect USDT',
      'INCORRECT_CONVERSION_SHARES': '⚠️ Incorrect Shares',
      'WRONG_REFERRER': '🔀 Wrong Referrer'
    };
    return names[type] || type.replace(/_/g, ' ');
  };

  const formatDate = (dateStr?: string) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-ZA', {
      year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit'
    });
  };

  // Get current sub-tab items, filtered and sorted
  const rawItems = activeSubTab === 'ALL' ? discrepancies : (grouped[activeSubTab] || []);
  const filteredItems = getSortedItems(getFilteredItems(rawItems));

  // Pagination
  const totalPages = Math.ceil(filteredItems.length / pageSize);
  const startIndex = (currentPage - 1) * pageSize;
  const paginatedItems = filteredItems.slice(startIndex, startIndex + pageSize);

  // Summary stats for current sub-tab
  const summaryStats = {
    totalMissingUSDT: filteredItems.reduce((sum, d) => sum + (d.expectedUSDT || 0), 0),
    totalMissingShares: filteredItems.reduce((sum, d) => sum + (d.expectedShares || 0), 0),
    uniqueUsers: new Set(filteredItems.map(d => d.referrerId)).size
  };

  // Export current view as CSV
  const exportCurrentView = () => {
    const lines = ['Date,Level,Triggered By,Referrer,Expected USDT,Expected Shares,Type'];
    for (const d of filteredItems) {
      lines.push([
        d.createdAt || '',
        d.level,
        d.triggeredByUsername || '',
        d.referrerUsername || '',
        d.expectedUSDT?.toFixed(6) || '0',
        d.expectedShares?.toFixed(6) || '0',
        d.type
      ].join(','));
    }
    const blob = new Blob([lines.join('\n')], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `discrepancies-${activeSubTab}-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="bg-gray-800 rounded-lg p-4">
      <h2 className="text-lg font-semibold mb-4">{title || 'All Discrepancies'} ({discrepancies.length})</h2>

      {discrepancies.length === 0 ? (
        <div className="text-center py-8">
          <p className="text-green-400 text-xl">✅ No discrepancies found!</p>
          <p className="text-white mt-2">All commissions are correctly recorded.</p>
        </div>
      ) : (
        <>
          {/* Sub-Tab Navigation */}
          <div className="flex flex-wrap gap-2 mb-4 border-b border-gray-700 pb-2">
            {discrepancyTypes.map((type) => {
              const count = grouped[type]?.length || 0;
              const isActive = activeSubTab === type;
              return (
                <button
                  key={type}
                  onClick={() => { setActiveSubTab(type); setCurrentPage(1); }}
                  className={`px-3 py-1.5 rounded-t text-sm font-medium transition-colors ${
                    isActive ? 'bg-amber-600 text-white' : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                  }`}
                >
                  {getTypeDisplayName(type)}
                  <span className={`ml-2 px-1.5 py-0.5 rounded-full text-sm ${isActive ? 'bg-amber-800' : 'bg-gray-600'}`}>
                    {count}
                  </span>
                </button>
              );
            })}
          </div>

          {/* Summary Stats */}
          <div className="grid grid-cols-3 gap-4 mb-4 p-3 bg-gray-700/50 rounded-lg">
            <div className="text-center">
              <p className="text-2xl font-bold text-amber-400">${summaryStats.totalMissingUSDT.toFixed(2)}</p>
              <p className="text-sm text-white">Total Missing USDT</p>
            </div>
            <div className="text-center">
              <p className="text-2xl font-bold text-cyan-400">{summaryStats.totalMissingShares.toFixed(4)}</p>
              <p className="text-sm text-white">Total Missing Shares</p>
            </div>
            <div className="text-center">
              <p className="text-2xl font-bold text-purple-400">{summaryStats.uniqueUsers}</p>
              <p className="text-sm text-white">Unique Users Affected</p>
            </div>
          </div>

          {/* Filters Row */}
          <div className="flex flex-wrap gap-3 mb-4 p-3 bg-gray-700/30 rounded-lg items-center">
            {/* Username Search */}
            <input
              type="text"
              placeholder="🔍 Search username..."
              value={filterUsername}
              onChange={(e) => { setFilterUsername(e.target.value); setCurrentPage(1); }}
              className="bg-gray-700 border border-gray-600 rounded px-2 py-1 text-sm w-40"
            />

            {/* Level Filter */}
            <select
              value={filterLevel ?? ''}
              onChange={(e) => { setFilterLevel(e.target.value ? Number(e.target.value) : null); setCurrentPage(1); }}
              className="bg-gray-700 border border-gray-600 rounded px-2 py-1 text-sm"
            >
              <option value="">All Levels</option>
              <option value="1">L1 Only</option>
              <option value="2">L2 Only</option>
              <option value="3">L3 Only</option>
            </select>

            {/* Min Amount Filter */}
            <input
              type="number"
              placeholder="Min $ amount"
              value={filterMinAmount ?? ''}
              onChange={(e) => { setFilterMinAmount(e.target.value ? Number(e.target.value) : null); setCurrentPage(1); }}
              className="bg-gray-700 border border-gray-600 rounded px-2 py-1 text-sm w-28"
            />

            {/* Quick Filters */}
            <div className="flex gap-1">
              {(['all', 'today', 'l1-only', 'high-value'] as const).map(qf => (
                <button
                  key={qf}
                  onClick={() => { setQuickFilter(qf); setCurrentPage(1); }}
                  className={`px-2 py-1 rounded text-sm ${
                    quickFilter === qf ? 'bg-amber-600' : 'bg-gray-600 hover:bg-gray-500'
                  }`}
                >
                  {qf === 'all' ? 'All' : qf === 'today' ? 'Today' : qf === 'l1-only' ? 'L1' : '>$10'}
                </button>
              ))}
            </div>

            {/* Clear Filters */}
            {(filterUsername || filterLevel || filterMinAmount || quickFilter !== 'all') && (
              <button
                onClick={() => { setFilterUsername(''); setFilterLevel(null); setFilterMinAmount(null); setQuickFilter('all'); setCurrentPage(1); }}
                className="text-gray-400 hover:text-white text-sm"
              >
                ✕ Clear
              </button>
            )}

            <div className="flex-1" />

            {/* Page Size */}
            <select
              value={pageSize}
              onChange={(e) => { setPageSize(Number(e.target.value)); setCurrentPage(1); }}
              className="bg-gray-700 border border-gray-600 rounded px-2 py-1 text-sm"
            >
              {PAGE_SIZE_OPTIONS.map(size => (
                <option key={size} value={size}>{size} per page</option>
              ))}
            </select>

            {/* Export Button */}
            <button
              onClick={exportCurrentView}
              className="bg-green-600 hover:bg-green-700 px-2 py-1 rounded text-sm"
            >
              📥 Export
            </button>
          </div>

          {/* Table */}
          <DiscrepancyTypeTable
            type={activeSubTab}
            items={paginatedItems}
            allItems={filteredItems}
            selectedCount={selectedCount}
            selectedForFix={selectedForFix}
            setSelectedForFix={setSelectedForFix}
            toggleSelection={toggleSelection}
            onBulkFix={onBulkFix}
            bulkFixing={bulkFixing}
            onBulkIgnore={onBulkIgnore}
            bulkIgnoring={bulkIgnoring}
            onFixCommission={onFixCommission}
            fixingCommission={fixingCommission}
            onIgnoreDiscrepancy={onIgnoreDiscrepancy}
            ignoringDiscrepancy={ignoringDiscrepancy}
            formatDate={formatDate}
            sortColumn={sortColumn}
            sortDirection={sortDirection}
            onSort={handleSort}
          />

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between mt-4 pt-4 border-t border-gray-700">
              <p className="text-sm text-white">
                Showing {startIndex + 1}-{Math.min(startIndex + pageSize, filteredItems.length)} of {filteredItems.length}
              </p>
              <div className="flex gap-2">
                <button onClick={() => setCurrentPage(1)} disabled={currentPage === 1}
                  className="px-3 py-1 bg-gray-700 rounded disabled:opacity-50 hover:bg-gray-600">First</button>
                <button onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1}
                  className="px-3 py-1 bg-gray-700 rounded disabled:opacity-50 hover:bg-gray-600">Prev</button>
                <span className="px-3 py-1 text-white">Page {currentPage} of {totalPages}</span>
                <button onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages}
                  className="px-3 py-1 bg-gray-700 rounded disabled:opacity-50 hover:bg-gray-600">Next</button>
                <button onClick={() => setCurrentPage(totalPages)} disabled={currentPage === totalPages}
                  className="px-3 py-1 bg-gray-700 rounded disabled:opacity-50 hover:bg-gray-600">Last</button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

// Single discrepancy type table component
function DiscrepancyTypeTable({
  type,
  items,
  allItems,
  selectedCount,
  selectedForFix,
  setSelectedForFix,
  toggleSelection,
  onBulkFix,
  bulkFixing,
  onBulkIgnore,
  bulkIgnoring,
  onFixCommission,
  fixingCommission,
  onIgnoreDiscrepancy,
  ignoringDiscrepancy,
  formatDate
}: {
  type: string;
  items: Discrepancy[];
  allItems?: Discrepancy[];
  selectedCount: number;
  selectedForFix?: Set<string>;
  setSelectedForFix?: React.Dispatch<React.SetStateAction<Set<string>>>;
  toggleSelection?: (key: string, discrepancy: Discrepancy) => void;
  onBulkFix?: (discrepancies: Discrepancy[]) => Promise<void>;
  bulkFixing?: boolean;
  onBulkIgnore?: (discrepancies: Discrepancy[]) => Promise<void>;
  bulkIgnoring?: boolean;
  onFixCommission?: FixCommissionHandler;
  fixingCommission?: string | null;
  onIgnoreDiscrepancy?: (discrepancy: Discrepancy) => Promise<void>;
  ignoringDiscrepancy?: string | null;
  formatDate: (dateStr?: string) => string;
  sortColumn?: 'date' | 'level' | 'amount' | 'referrer' | null;
  sortDirection?: 'asc' | 'desc';
  onSort?: (column: 'date' | 'level' | 'amount' | 'referrer') => void;
}) {
  // Check if this is an INCORRECT type (has actual values)
  const isIncorrectType = type.includes('INCORRECT');
  // Check if this is a WRONG_REFERRER type (paid to wrong person)
  const isWrongReferrerType = type === 'WRONG_REFERRER';
  // Check if this is a MISSING type (can be fixed)
  const isMissingType = type === 'MISSING_COMMISSION' || type === 'MISSING_CONVERSION_COMMISSION' || isWrongReferrerType;

  // All items can be selected for bulk actions (fix or ignore)
  const hasSelectableItems = items.length > 0 && (isMissingType || isIncorrectType);

  console.log('🔍 DiscrepancyTypeTable render:', {
    type,
    itemsLength: items.length,
    hasSelectableItems,
    hasSelectedForFix: !!selectedForFix,
    hasSetSelectedForFix: !!setSelectedForFix,
    hasToggleSelection: !!toggleSelection,
    hasOnBulkFix: !!onBulkFix,
    hasOnBulkIgnore: !!onBulkIgnore
  });

  return (
    <div>
      {/* Bulk Action Buttons */}
      {hasSelectableItems && selectedForFix && toggleSelection && (
        <div className="flex items-center justify-between gap-2 mb-3">
          {/* Quick Select Buttons */}
          <div className="flex items-center gap-2">
            {onBulkFix && setSelectedForFix && (
              <button
                onClick={() => {
                  console.log('🔍 Select 500 Fix clicked');
                  // Select top 500 fixable items from ALL items (not just current page)
                  const itemsToSearch = allItems || items;
                  console.log('🔍 Total items to search:', itemsToSearch.length);
                  console.log('🔍 Item types in search:', [...new Set(itemsToSearch.map(d => d.type))]);
                  const fixableItems = itemsToSearch.filter(d => {
                    // Missing commissions are always fixable
                    if (d.type === 'MISSING_COMMISSION' || d.type === 'MISSING_CONVERSION_COMMISSION' || d.type === 'WRONG_REFERRER') {
                      return true;
                    }
                    // INCORRECT types are fixable if difference > 0 (underpayment)
                    if (d.type.includes('INCORRECT') && (d.difference || 0) > 0) {
                      return true;
                    }
                    return false;
                  }).slice(0, BULK_SELECTION_LIMIT);
                  console.log('🔍 Fixable items found:', fixableItems.length);

                  if (fixableItems.length === 0) {
                    alert('⚠️ No fixable items found on this tab!\n\nThe "Select 500 Fix" button only works for:\n- Missing Commission\n- Missing Conversion Commission\n- Wrong Referrer\n- Incorrect amounts with positive difference (underpayments)\n\nYou may be viewing overpayments which need to be IGNORED, not FIXED.\n\nPlease use the "Select 500 Ignore" button instead, or switch to a different sub-tab.');
                    return;
                  }

                  // Update state in a single operation
                  setSelectedForFix(prev => {
                    console.log('🔍 Previous selection size:', prev.size);
                    const next = new Set(prev);
                    let added = 0;
                    for (const d of fixableItems) {
                      if (added >= BULK_SELECTION_LIMIT) break;
                      const key = `${d.sourceType}-${d.sourceId}-L${d.level}-${d.referrerId}`;
                      if (!next.has(key)) {
                        next.add(key);
                        added++;
                        console.log('🔍 Added:', key);
                      }
                    }
                    console.log('🔍 New selection size:', next.size, 'Added:', added);
                    return next;
                  });
                }}
                disabled={bulkFixing || bulkIgnoring}
                className="bg-green-700 hover:bg-green-600 disabled:bg-gray-600 px-3 py-1 rounded text-sm font-semibold"
              >
                ✅ Select 500 Fix
              </button>
            )}
            {onBulkIgnore && setSelectedForFix && (
              <button
                onClick={() => {
                  console.log('🔍 Select 500 Ignore clicked');
                  // Select top 500 ignorable items from ALL items (not just current page)
                  const itemsToSearch = allItems || items;
                  console.log('🔍 Total items to search:', itemsToSearch.length);
                  const ignorableItems = itemsToSearch.filter(d =>
                    (d.type === 'INCORRECT_USDT' || d.type === 'INCORRECT_SHARES' || d.type === 'INCORRECT_CONVERSION_USDT' || d.type === 'INCORRECT_CONVERSION_SHARES')
                  ).slice(0, BULK_SELECTION_LIMIT);
                  console.log('🔍 Ignorable items found:', ignorableItems.length);

                  // Update state in a single operation
                  setSelectedForFix(prev => {
                    console.log('🔍 Previous selection size:', prev.size);
                    const next = new Set(prev);
                    let added = 0;
                    for (const d of ignorableItems) {
                      if (added >= BULK_SELECTION_LIMIT) break;
                      const key = `${d.sourceType}-${d.sourceId}-L${d.level}-${d.referrerId}`;
                      if (!next.has(key)) {
                        next.add(key);
                        added++;
                        console.log('🔍 Added:', key);
                      }
                    }
                    console.log('🔍 New selection size:', next.size, 'Added:', added);
                    return next;
                  });
                }}
                disabled={bulkFixing || bulkIgnoring}
                className="bg-orange-700 hover:bg-orange-600 disabled:bg-gray-600 px-3 py-1 rounded text-sm font-semibold"
              >
                🙈 Select 500 Ignore
              </button>
            )}
            {setSelectedForFix && (
              <button
                onClick={() => {
                  // Clear all selections
                  setSelectedForFix(new Set());
                }}
                disabled={selectedCount === 0 || bulkFixing || bulkIgnoring}
                className="bg-gray-700 hover:bg-gray-600 disabled:bg-gray-800 px-3 py-1 rounded text-sm"
              >
                Clear All
              </button>
            )}
          </div>

          {/* Action Buttons */}
          <div className="flex items-center gap-2">
            <span className="text-sm text-white">
              {selectedCount}/{BULK_SELECTION_LIMIT} selected
            </span>
            {onBulkFix && (
              <button
                onClick={() => onBulkFix(items)}
                disabled={selectedCount === 0 || bulkFixing || bulkIgnoring}
                className="bg-green-600 hover:bg-green-700 disabled:bg-gray-600 px-3 py-1 rounded text-sm font-semibold"
              >
                {bulkFixing ? '⏳ Fixing...' : `🔧 Fix Selected (${selectedCount})`}
              </button>
            )}
            {onBulkIgnore && (
              <button
                onClick={() => onBulkIgnore(items)}
                disabled={selectedCount === 0 || bulkFixing || bulkIgnoring}
                className="bg-red-600 hover:bg-red-500 disabled:bg-gray-700 px-3 py-1 rounded text-sm font-semibold"
              >
                {bulkIgnoring ? '⏳ Ignoring...' : `🙈 Ignore Selected (${selectedCount})`}
              </button>
            )}
          </div>
        </div>
      )}

      <div className="overflow-x-auto max-h-[500px] overflow-y-auto">
        <table className="w-full text-sm">
          <thead className="sticky top-0 bg-gray-800">
            <tr className="text-left text-white border-b border-gray-700">
              {/* Checkbox column for selectable items */}
              {hasSelectableItems && selectedForFix && <th className="py-2 px-2 w-8">☐</th>}
              <th className="py-2 px-2">Date</th>
              <th className="py-2 px-2">Level</th>
              <th className="py-2 px-2">Triggered By</th>
              <th className="py-2 px-2">Referrer (Receives)</th>
              {isWrongReferrerType && <th className="py-2 px-2 text-red-400">Wrong Recipient</th>}
              <th className="py-2 px-2">Expected USDT</th>
              {isIncorrectType && <th className="py-2 px-2">Actual USDT</th>}
              <th className="py-2 px-2">Expected Shares</th>
              {isIncorrectType && <th className="py-2 px-2">Actual Shares</th>}
              {isIncorrectType && <th className="py-2 px-2">Difference</th>}
              {/* Failure Reason column for missing commissions */}
              {isMissingType && <th className="py-2 px-2 text-amber-400">Reason</th>}
              {(isMissingType && onFixCommission) || (isIncorrectType && onIgnoreDiscrepancy) ? <th className="py-2 px-2">Action</th> : null}
            </tr>
          </thead>
          <tbody>
            {items.map((d, i) => {
              const fixKey = `${d.sourceType}-${d.sourceId}-L${d.level}-${d.referrerId}`;
              const isFixing = fixingCommission === fixKey;
              const isIgnoring = ignoringDiscrepancy === fixKey;
              const triggerAmount = d.purchaseAmount || d.conversionAmount || 0;
              const isSelected = selectedForFix?.has(fixKey) || false;

              return (
                <tr key={i} className={`border-b border-gray-700/50 ${isSelected ? 'bg-green-900/30' : ''}`}>
                  {/* Checkbox for all selectable items */}
                  {hasSelectableItems && selectedForFix && toggleSelection && (
                    <td className="py-2 px-2">
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => toggleSelection(fixKey, d)}
                        disabled={!isSelected && selectedCount >= BULK_SELECTION_LIMIT}
                        className="w-4 h-4 accent-green-500"
                      />
                    </td>
                  )}
                  <td className="py-2 px-2 text-white text-sm">{formatDate(d.createdAt)}</td>
                  <td className="py-2 px-2">L{d.level}</td>
                  <td className="py-2 px-2">
                    <span className="text-cyan-300">{d.triggeredByUsername || 'Unknown'}</span>
                    {triggerAmount > 0 && (
                      <span className="text-white ml-1">(${triggerAmount.toFixed(2)})</span>
                    )}
                  </td>
                  <td className="py-2 px-2">{d.referrerUsername} (ID: {d.referrerId})</td>
                  {isWrongReferrerType && (
                    <td className="py-2 px-2 text-red-400">
                      {d.wrongReferrerUsername} (ID: {d.wrongReferrerId})
                    </td>
                  )}
                  <td className="py-2 px-2">${d.expectedUSDT?.toFixed(6) || '-'}</td>
                  {isIncorrectType && (
                    <td className="py-2 px-2 text-amber-300">
                      ${d.actualUSDT?.toFixed(6) || '-'}
                    </td>
                  )}
                  <td className="py-2 px-2">{d.expectedShares?.toFixed(6) || '-'}</td>
                  {isIncorrectType && (
                    <td className="py-2 px-2 text-amber-300">
                      {d.actualShares?.toFixed(6) || '-'}
                    </td>
                  )}
                  {isIncorrectType && (
                    <td className={`py-2 px-2 ${(d.difference || 0) > 0 ? 'text-red-400' : 'text-green-400'}`}>
                      {d.difference !== undefined ? (d.difference > 0 ? '+' : '') + d.difference.toFixed(6) : '-'}
                    </td>
                  )}
                  {/* Failure Reason column for missing commissions */}
                  {isMissingType && (
                    <td className="py-2 px-2 text-sm">
                      {d.failureReason ? (
                        <span className="text-amber-400" title={d.failureReason}>
                          {d.failureReason.length > 40 ? d.failureReason.substring(0, 40) + '...' : d.failureReason}
                        </span>
                      ) : (
                        <span className="text-gray-500">Unknown</span>
                      )}
                    </td>
                  )}
                  {isMissingType && onFixCommission && (
                    <td className="py-2 px-2">
                      <button
                        onClick={() => onFixCommission({
                          type: d.sourceType as 'purchase' | 'conversion',
                          sourceId: d.sourceId || '',
                          referredId: d.referredId || 0,
                          referrerId: d.referrerId,
                          level: d.level,
                          expectedUSDT: d.expectedUSDT || 0,
                          expectedShares: d.expectedShares || 0,
                          baseAmount: d.baseAmount || 0,
                          phaseId: d.phaseId || 2,
                          sharePrice: d.sharePrice || 10,
                          ...(d.type === 'WRONG_REFERRER' && {
                            fixType: 'WRONG_REFERRER' as const,
                            wrongReferrerId: d.wrongReferrerId,
                            existingCommissionId: d.existingCommissionId
                          }),
                          // v4.8.22 #6: UI-only preview block for the confirmation modal
                          display: {
                            referrerUsername: d.referrerUsername,
                            wrongReferrerUsername: d.wrongReferrerUsername,
                            triggeredByUsername: d.triggeredByUsername,
                            triggerAmount: d.purchaseAmount || d.conversionAmount,
                            createdAt: d.createdAt ? formatDate(d.createdAt) : undefined
                          }
                        })}
                        disabled={isFixing}
                        className={`${isWrongReferrerType ? 'bg-red-600 hover:bg-red-700' : 'bg-amber-600 hover:bg-amber-700'} disabled:bg-gray-600 px-2 py-1 rounded text-sm font-semibold`}
                      >
                        {isFixing ? '⏳ Fixing...' : isWrongReferrerType ? '🔄 Reassign' : '🔧 Fix'}
                      </button>
                    </td>
                  )}
                  {isIncorrectType && (
                    <td className="py-2 px-2">
                      {/* Underpayment (difference > 0): show Fix button */}
                      {(d.difference || 0) > 0 && onFixCommission && (
                        <button
                          onClick={() => onFixCommission({
                            type: d.sourceType as 'purchase' | 'conversion',
                            sourceId: d.sourceId || '',
                            referredId: d.referredId || 0,
                            referrerId: d.referrerId,
                            level: d.level,
                            expectedUSDT: d.type.includes('USDT') ? (d.difference || 0) : 0,
                            expectedShares: d.type.includes('SHARES') ? (d.difference || 0) : 0,
                            baseAmount: d.baseAmount || 0,
                            phaseId: d.phaseId || 2,
                            sharePrice: d.sharePrice || 10,
                            discrepancyType: d.type,
                            // v4.8.22 #6: UI-only preview block for the confirmation modal
                            display: {
                              referrerUsername: d.referrerUsername,
                              triggeredByUsername: d.triggeredByUsername,
                              triggerAmount: d.purchaseAmount || d.conversionAmount,
                              createdAt: d.createdAt ? formatDate(d.createdAt) : undefined
                            }
                          })}
                          disabled={isFixing}
                          className="bg-amber-600 hover:bg-amber-700 disabled:bg-gray-600 px-2 py-1 rounded text-sm font-semibold"
                          title="Add missing commission and mark as resolved"
                        >
                          {isFixing ? '⏳ Fixing...' : '🔧 Fix'}
                        </button>
                      )}
                      {/* Overpayment (difference < 0): show Ignore button */}
                      {(d.difference || 0) < 0 && onIgnoreDiscrepancy && (
                        <button
                          onClick={() => onIgnoreDiscrepancy(d)}
                          disabled={isIgnoring}
                          className="bg-gray-600 hover:bg-gray-500 disabled:bg-gray-700 px-2 py-1 rounded text-sm font-semibold"
                          title="Accept this overpayment and hide from future audits"
                        >
                          {isIgnoring ? '⏳ Ignoring...' : '🙈 Ignore'}
                        </button>
                      )}
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

